// @ts-nocheck
import PlayerExtraDialog from "@/components/extras/PlayerExtraDialog";
import { displayHandle } from "@/lib/handles";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Plus, Trash2, AlertTriangle, Check, Pencil, ChevronUp, ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { RichTextDisplay } from "@/design-system/forms/RichTextEditor";
import { evaluateDiscountPolicies } from "@/lib/discountPolicy";
import {
  Cart,
  CartItem,
  CartExtra,
  PaymentMethod,
  itemTotal,
  tierWaivesFees,
} from "@/types/registration";
import { perkLabels, DEFAULT_TIER_PERKS } from "@/lib/schedulingPerks";
import {
  loadCart,
  saveCart,
  clearCart,
  emptyCart,
  newItem,
  loadWizardState,
  saveWizardState,
  saveWizardDraft,
} from "@/lib/registrationCart";

import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { useEventDivisionGuard, useOrganiserContact } from "@/hooks/useEventDivisionGuard";
import {
  parseTeamName,
  normalizeForMatch,
  type ParsedTeamName,
} from "@/lib/teamNameParser";
import TeamNameEntry, {
  buildDisplayName,
  isItemComplete,
  isValidHomeZip,
  type DivisionOption,
  type PreviousTeam,
} from "@/components/events/TeamNameEntry";

type StepId = "team" | "coach" | "scheduling" | "extras" | "pool" | "payment" | "account" | "review";

import {
  getRegistrationAvailability,
  isRegistrationBlocked,
  REGISTRATION_AVAILABILITY_FIELDS,
} from "@/lib/registrationAvailability";

const SCHEDULING_EVENT_FIELDS =
  "scheduling_tiers_enabled,priority_tier_price,club_priority_tier_price,club_priority_min_teams,showcase_tier_enabled,showcase_tier_price,priority_tier_name,club_priority_tier_name,showcase_tier_name,priority_tier_description,club_priority_tier_description,showcase_tier_description,priority_tier_perks,club_priority_tier_perks,showcase_tier_perks,priority_tier_waives_fees,club_priority_tier_waives_fees,showcase_tier_waives_fees";

// Display-only fallback (used by the cart summary line). Authoritative
// waiving is per-item (item.scheduling_tier_waives_fees) and per-event config.
const TIER_WAIVES_FEES = (tier?: string | null) =>
  tier === "priority" || tier === "club_priority" || tier === "showcase";

function constraintFeeForDisplay(
  c: any,
  constraintTypePricing: Record<string, any> | null | undefined
): number {
  const ct = c?.constraint_type;
  const overrides = constraintTypePricing ?? {};
  if (ct && overrides[ct] !== undefined && overrides[ct] !== null) {
    return Number(overrides[ct]);
  }
  return Number(c?.fee ?? 0);
}

// 3% card processing fee, applied to card payments only (never check, cash
// or venmo). Kept in sync with supabase/functions/_shared/stripe-line-items.ts.
const CARD_FEE_RATE = 0.03;

const TIER_LABEL: Record<string, string> = {
  standard: "Standard",
  priority: "Priority",
  club_priority: "Club Priority",
  showcase: "Showcase",
};

const sb = supabase as any;

function money(n: number) {
  return `$${(n || 0).toFixed(2)}`;
}

function profileNameParts(profile: any, user: any) {
  const metadata = user?.user_metadata ?? {};
  const combinedName = String(
    profile?.name || metadata.name || metadata.full_name || ""
  ).trim();
  const [nameFirst = "", ...nameRest] = combinedName.split(/\s+/);
  return {
    firstName: String(profile?.first_name || metadata.first_name || nameFirst || "").trim(),
    lastName: String(profile?.last_name || metadata.last_name || nameRest.join(" ") || "").trim(),
  };
}


// Any item that is not complete is a placeholder / abandoned entry. Only ONE
// of these may ever exist (the trailing "next team" slot); it must never count
// toward completeness gating, cart lines, or totals.
function isBlankItem(i: CartItem): boolean {
  return !isItemComplete(i);
}

// Keep every completed team, drop all incomplete leftovers, then append exactly
// one fresh blank entry slot (optionally an already-created one).
function pruneItems(items: CartItem[], trailing: CartItem): CartItem[] {
  return [...items.filter(isItemComplete), trailing];
}


// Phone helpers: store digits only, display as xxx-xxx-xxxx.
export const phoneDigits = (v: string) => (v || "").replace(/\D/g, "").slice(0, 10);
export const formatPhoneDisplay = (v: string) => {
  const d = phoneDigits(v);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};



// Pay-later config is event-level, never copied per cart item.
function dueDateFor(event: any): Date | null {
  if (!event?.start_date || event?.pay_later_due_days_before_event == null)
    return null;
  const d = new Date(event.start_date);
  d.setDate(d.getDate() - event.pay_later_due_days_before_event);
  return d;
}


export default function RegistrationCart() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, profile, isLoading: authLoading } = useAuth();

  const [cart, setCart] = useState<Cart | null>(null);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);

  const [step, setStep] = useState<StepId>("team");
  // The cart is initialised exactly once per (event, user). Auth token
  // refreshes fire on tab focus and must never re-run it — that's what used to
  // wipe the coach's position when they switched away and came back.
  const initRef = useRef<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Scheduling gating is per-team (keyed by cart item client_id).
  const [schedulingGate, setSchedulingGate] = useState<Record<string, string>>({});
  const [confirmedEmptyTiers, setConfirmedEmptyTiers] = useState<string[]>([]);


  // Scroll to the top whenever the registration step changes so users don't
  // land at the bottom of the previous step's content.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);

  // Load event
  const { data: event, isLoading: loadingEvent } = useQuery({
    queryKey: ["cart-event", slug],
    enabled: !!slug,
    queryFn: async () => {
      let q = await sb
        .from("events")
        .select(
          `id,name,slug,start_date,end_date,allow_pay_later,pay_later_methods,pay_later_instructions,pay_later_check_instructions,pay_later_venmo_note,pay_later_venmo_handle,pay_later_due_days_before_event,pay_later_late_fee,organization_id,registration_levels_enabled,registration_levels_required,registration_levels_help_text,${REGISTRATION_AVAILABILITY_FIELDS},${SCHEDULING_EVENT_FIELDS}`
        )
        .eq("slug", slug)
        .maybeSingle();
      if (!q.data) {
        q = await sb
          .from("events")
          .select(
            `id,name,slug,start_date,end_date,allow_pay_later,pay_later_methods,pay_later_instructions,pay_later_check_instructions,pay_later_venmo_note,pay_later_venmo_handle,pay_later_due_days_before_event,pay_later_late_fee,organization_id,registration_levels_enabled,registration_levels_required,registration_levels_help_text,${REGISTRATION_AVAILABILITY_FIELDS},${SCHEDULING_EVENT_FIELDS}`
          )
          .eq("id", slug)
          .maybeSingle();
      }
      return q.data;
    },
  });

  // Automatic org discount policies (e.g. multi-team). These apply to
  // ENTRY FEES ONLY — never to scheduling requests, tier fees, or extras.
  const { data: discountPolicies } = useQuery({
    queryKey: ["cart-discount-policies", event?.organization_id],
    enabled: !!event?.organization_id,
    queryFn: async () => {
      const { data } = await (sb as any)
        .from("event_discount_policies")
        .select("*")
        .eq("organization_id", event!.organization_id)
        .eq("is_active", true);
      return data ?? [];
    },
  });

  const divisionGuard = useEventDivisionGuard(event?.id);
  const organiserContact = useOrganiserContact(event?.organization_id);

  // NO AUTH GATE — anonymous users can build the cart and sign in/up at the Account step.

  // Initialize cart (once per event + signed-in user)
  useEffect(() => {
    if (!event) return;
    const ownerId = user?.id ?? "anon";
    const initKey = `${event.id}:${ownerId}`;
    if (initRef.current === initKey) return;
    initRef.current = initKey;
    let c = loadCart();

    if (!c || (c.registrant_user_id !== ownerId && c.registrant_user_id !== "anon")) {
      c = emptyCart({
        id: ownerId,
        email: user?.email ?? "",
        name: profile?.name ?? "",
      });
    }
    // If user just signed in, take ownership of an anon cart
    if (user && c.registrant_user_id === "anon") {
      c = {
        ...c,
        registrant_user_id: user.id,
        registrant_email: c.registrant_email || user.email || "",
      };
    }
    // Legacy carts stored the payment method on an item. Payment is a single
    // cart-level choice — hoist the first one found and strip the rest.
    const legacyMethod = (c.items as any[]).find((i) => i.payment_method_chosen)
      ?.payment_method_chosen;
    const legacyNotes = (c.items as any[]).find((i) => i.payment_promise_notes)
      ?.payment_promise_notes;
    if (legacyMethod || legacyNotes) {
      c = {
        ...c,
        payment_method_chosen: c.payment_method_chosen ?? legacyMethod,
        payment_promise_notes: c.payment_promise_notes ?? legacyNotes,
      };
    }
    c = {
      ...c,
      items: c.items.map((i) => {
        const {
          payment_method_chosen: _m,
          payment_promise_notes: _n,
          event_allow_pay_later: _a,
          event_pay_later_methods: _pm,
          event_pay_later_instructions: _pi,
          event_pay_later_due_days_before_event: _pd,
          event_pay_later_late_fee: _pl,
          event_pay_later_venmo_handle: _pv,
          ...rest
        } = i as any;
        return rest as CartItem;
      }),
    };
    // Restore where the coach was: step, which team was open, and any
    // partially-entered team that was never committed.
    const wiz = loadWizardState();
    const eventKey = String(event.slug ?? event.id);
    const sameEvent = wiz?.event_slug === eventKey;

    const freshItem = () =>
      newItem({
        event_id: event.id,
        event_name: event.name,
        event_start_date: event.start_date,
      });
    // Keep the in-progress (incomplete) team the coach was editing as the
    // trailing entry; otherwise start a fresh blank one.
    const resume =
      sameEvent && wiz?.editingClientId
        ? c.items.find(
            (i) => i.client_id === wiz.editingClientId && !isItemComplete(i)
          )
        : null;
    const trailing = resume ?? freshItem();
    // Strip every other incomplete leftover and keep exactly one trailing entry.
    c = { ...c, items: pruneItems(c.items, trailing) };

    const restoredEditing =
      sameEvent &&
      wiz?.editingClientId &&
      c.items.some((i) => i.client_id === wiz.editingClientId)
        ? wiz.editingClientId
        : trailing.client_id;
    setEditingClientId(restoredEditing);
    if (sameEvent && wiz?.step) setStep(wiz.step as StepId);
    saveWizardState({
      event_slug: eventKey,
      step: sameEvent && wiz?.step ? wiz.step : "team",
      editingClientId: restoredEditing,
    });



    // Prefill registrant details from the signed-in profile whenever the cart
    // doesn't already carry them (stored carts often have blanks).
    if (profile || user) {
      const { firstName, lastName } = profileNameParts(profile, user);
      c = {
        ...c,
        registrant_first_name:
          c.registrant_first_name?.trim() || firstName,
        registrant_last_name:
          c.registrant_last_name?.trim() || lastName,
        // Signed in: the account address is the single registrant email.
        registrant_email:
          user?.email || c.registrant_email || (profile as any)?.email || "",
        registrant_phone: c.registrant_phone || (profile as any)?.phone || "",
      };
    }

    setCart(c);


    // Identity-only deps: object identity changes (e.g. a token refresh on tab
    // focus) must not re-initialise and lose the coach's position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, event?.id]);

  // Profile can resolve after init — fill blanks only, never overwrite typing.
  useEffect(() => {
    if (!cart || (!profile && !user)) return;
    const { firstName, lastName } = profileNameParts(profile, user);
    const patch: Partial<Cart> = {};
    if (!cart.registrant_first_name?.trim() && firstName) patch.registrant_first_name = firstName;
    if (!cart.registrant_last_name?.trim() && lastName) patch.registrant_last_name = lastName;
    // Signed in: the account address always wins — there is exactly one
    // registrant email in the flow.
    if (user?.email) {
      if (cart.registrant_email !== user.email) patch.registrant_email = user.email;
    } else if (!cart.registrant_email && (profile as any)?.email) {
      patch.registrant_email = (profile as any).email;
    }
    if (!cart.registrant_phone && (profile as any)?.phone)
      patch.registrant_phone = (profile as any).phone;
    if (Object.keys(patch).length > 0) setCart((c) => (c ? { ...c, ...patch } : c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, user?.id, cart?.id, cart?.items.length]);

  useEffect(() => {
    if (cart) saveCart(cart);
  }, [cart]);

  // Persist wizard position so leaving the tab never loses it. Never write
  // before init — that would clobber the stored position we're restoring from.
  useEffect(() => {
    if (!initRef.current || !editingClientId) return;
    saveWizardState({ step, editingClientId });
  }, [step, editingClientId]);


  // Once the coach is signed in, the Account step no longer exists — move them
  // straight to Review with the cart intact instead of stranding them.
  useEffect(() => {
    if (user && !authLoading && step === "account") setStep("review");
  }, [user, authLoading, step]);


  const editingItem = useMemo(
    () => cart?.items.find((i) => i.client_id === editingClientId) ?? null,
    [cart, editingClientId]
  );

  // Safety net: on any step past "team", never sit with a dangling editing id
  // (it renders as a permanent loading skeleton).
  useEffect(() => {
    if (step === "team" || !cart || editingItem) return;
    const fallback = [...cart.items].reverse().find(isItemComplete) ?? cart.items[cart.items.length - 1];
    if (fallback) setEditingClientId(fallback.client_id);
  }, [step, cart, editingItem]);


  // Load teams already registered for this event (for constraint team_select inputs)
  const { data: eventTeams } = useQuery({
    queryKey: ["event-registered-teams", event?.id],
    enabled: !!event?.id,
    queryFn: async () => {
      const { data, error } = await sb.rpc("list_event_teams", {
        p_event_id: event.id,
      });
      if (error) {
        console.error("list_event_teams failed", error);
        return [];
      }
      return (data ?? []) as Array<{
        team_id: string;
        display_name: string;
        age_group: string | null;
        gender: string | null;
      }>;
    },
  });

  // Load registration levels (pools) for this event
  const { data: levels } = useQuery({
    queryKey: ["event-registration-levels", event?.id],
    enabled: !!event?.id,
    queryFn: async () => {
      const { data } = await sb
        .from("event_registration_levels")
        .select("id,label,sort_order")
        .eq("event_id", event.id)
        .order("sort_order", { ascending: true });
      return (data ?? []) as Array<{ id: string; label: string; sort_order: number }>;
    },
  });

  const updateItem = (patch: Partial<CartItem>) => {
    if (!cart || !editingItem) return;
    setCart({
      ...cart,
      items: cart.items.map((i) =>
        i.client_id === editingItem.client_id ? { ...i, ...patch } : i
      ),
    });
  };

  const blankItem = () =>
    newItem({
      event_id: event.id,
      event_name: event.name,
      event_start_date: event.start_date,
    });


  const addAnotherTeam = () => {
    if (!cart || !event) return;
    const item = blankItem();
    setCart((c) => (c ? { ...c, items: [...c.items, item] } : c));
    setEditingClientId(item.client_id);
    setStep("team");
  };

  // Commit a team: apply the patch to the item AND open a fresh blank team in
  // ONE atomic state update. Doing these as two separate setCart calls used a
  // stale `cart` closure, so the patch was silently discarded and the confirm
  // button appeared to do nothing.
  const commitTeam = (clientId: string, patch: Partial<CartItem>) => {
    if (!event) return;
    const fresh = blankItem();
    setCart((c) =>
      c
        ? {
            ...c,
            // Apply the patch, keep only completed teams, then append exactly
            // one fresh blank slot for the next entry.
            items: pruneItems(
              c.items.map((i) =>
                i.client_id === clientId ? { ...i, ...patch } : i
              ),
              fresh
            ),
          }
        : c
    );
    setEditingClientId(fresh.client_id);
    setStep("team");
  };



  const removeItem = (clientId: string) => {
    if (!cart) return;
    const remaining = cart.items.filter((i) => i.client_id !== clientId);
    if (remaining.length === 0) {
      // Removing the last team resets the cart to a fresh blank team so the
      // buyer can start over instead of getting stuck.
      if (!event) return;
      const fresh = newItem({
        event_id: event.id,
        event_name: event.name,
        event_start_date: event.start_date,
      });

      setCart({ ...cart, items: [fresh] });
      setEditingClientId(fresh.client_id);
      setStep("team");
      return;
    }
    setCart({ ...cart, items: remaining });
    if (editingClientId === clientId && remaining[0]) {
      setEditingClientId(remaining[0].client_id);
    }
  };


  const submit = async () => {
    if (!cart) return;
    const submitItems = cart.items.filter(isItemComplete);
    if (submitItems.length === 0) {
      toast.error("Add at least one team before submitting.");
      return;
    }
    const missingZip = submitItems.find((i) => !isValidHomeZip(i.team_home_zip));
    if (missingZip) {
      toast.error("Enter a 5-digit Team home ZIP for every team before continuing.");
      setEditingClientId(missingZip.client_id);
      setStep("team");
      return;
    }
    const cartPaymentMethod = cart.payment_method_chosen ?? "online";
    setSubmitting(true);
    try {
      const payload = {

        items: submitItems.map((i) => ({
          client_id: i.client_id,
          event_id: i.event_id,
          team_id: i.team_id,
          recruitlook_team_id: i.recruitlook_team_id || i.team_id || null,
          team_name: i.team_display_name || i.team_name,
          team_display_name: i.team_display_name,
          club_id: i.club_id ?? null,
          new_club_name: i.new_club_name ?? null,
          new_short_name: i.new_short_name ?? null,
          raw_team_input: (i as any).raw_team_input ?? null,
          team_home_zip: (i as any).team_home_zip ?? null,
          age_group: (i as any).age_group ?? null,
          catalog_division_id: i.catalog_division_id,
          gender: i.gender,
          base_price: i.base_price,
          division_label: i.division_label,
          matched_via: i.matched_via ?? null,
          match_score: i.match_score ?? null,
          rejected_match_ids: i.rejected_match_ids ?? [],
          coach: {
            name: i.coach_name,
            email: i.coach_email,
            phone: i.coach_phone,
            registrant_is_coach: i.registrant_is_coach,
          },
          // One payment choice for the whole cart — applied to every registration.
          payment_method_chosen: cartPaymentMethod,
          payment_promise_notes: cart.payment_promise_notes,

          requested_constraints: i.requested_constraints,
          scheduling_tier: i.scheduling_tier ?? "standard",
          scheduling_tier_fee: Number(i.scheduling_tier_fee || 0),
          extras: i.extras,
          pool: i.pool ?? null,
        })),
        coupon_code: cart.coupon_code,
        registrant_first_name: cart.registrant_first_name,
        registrant_last_name: cart.registrant_last_name,
        payment_method_chosen: cartPaymentMethod,
        payment_promise_notes: cart.payment_promise_notes,
      };

      const { data, error } = await sb.functions.invoke("register-team-cart", {
        body: payload,
      });
      if (error) {
        const ctx = (error as any).context;
        // Read the body first — the duplicate-team response is a 409 with a
        // plain-language message, not the older conflicts payload.
        let detail = "";
        try {
          if (ctx && typeof ctx.text === "function") detail = await ctx.text();
          else if (ctx?.body) detail = String(ctx.body);
        } catch {
          /* ignore body read failure */
        }
        let parsedBody: any = null;
        try {
          parsedBody = detail ? JSON.parse(detail) : null;
        } catch {
          /* not json */
        }
        if (parsedBody?.duplicate_teams?.length) {
          toast.error(parsedBody.error);
          setSubmitError(parsedBody.error);
          setSubmitting(false);
          return;
        }
        console.error("register-team-cart failed", {
          status: ctx?.status,
          message: error.message,
          body: detail,
          data,
        });
        const parsedMsg = parsedBody?.error ?? "";
        throw new Error(
          parsedMsg ||
            detail ||
            error.message ||
            "Registration failed. Please try again."
        );
      }
      if (data?.error) throw new Error(String(data.error));
      const newCartId = data?.cart_id ?? "";
      if (!newCartId) {
        console.error("register-team-cart returned no cart_id", data);
        throw new Error(
          "Registration did not save (no cart was created). Please try again."
        );
      }
      const hasOnline =
        cartPaymentMethod === "online" &&
        submitItems.some((i) => itemTotal(i) > 0);

      if (hasOnline && newCartId) {
        // Kick off Stripe Checkout for all online unpaid registrations in this cart
        const { data: ck, error: ckErr } = await sb.functions.invoke(
          "create-stripe-checkout-session",
          { body: { cart_id: newCartId } }
        );
        if (ckErr || !ck?.ok || !ck?.checkout_url) {
          // Surface the function's real status + body instead of the opaque
          // "Edge Function returned a non-2xx status code".
          let status: number | undefined;
          let bodyText = "";
          const ctx: any = (ckErr as any)?.context;
          if (ctx) {
            status = ctx.status;
            try {
              bodyText = await ctx.clone?.().text?.();
            } catch {
              /* body already consumed */
            }
          }
          let parsed: any = null;
          try {
            parsed = bodyText ? JSON.parse(bodyText) : null;
          } catch {
            /* not json */
          }
          console.error("create-stripe-checkout-session failed", {
            status,
            message: ckErr?.message,
            body: bodyText,
            data: ck,
            cart_id: newCartId,
          });
          toast.error(
            parsed?.error ??
              ck?.error ??
              ckErr?.message ??
              "Could not start payment. Your registration was saved — please retry from your dashboard."
          );
          clearCart();
          navigate(
            `/events/${slug}/register/success?cart_id=${newCartId}&payment_error=1`
          );
          return;
        }

        clearCart();
        window.location.href = ck.checkout_url;
        return;
      }
      clearCart();
      navigate(
        `/events/${slug}/register/success?cart_id=${newCartId}`
      );
    } catch (e: any) {
      const msg = e?.message ?? "Registration failed";
      console.error("Registration submit failed", e);
      setSubmitError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  if (loadingEvent || !event || !cart) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  // Registration controls (accept_online_registrations / external URL / date
  // window) are enforced here too, so a direct link to this route refuses just
  // like the button on the event page does.
  const availability = getRegistrationAvailability(event as any);
  if (isRegistrationBlocked(availability) || availability.state === "external") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 space-y-3 text-center">
            <h1 className="text-xl font-bold">
              {availability.state === "external"
                ? "Registration is handled off site"
                : "Registration is not open for this event"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {availability.message}
            </p>
            {availability.state === "external" ? (
              <a
                href={availability.externalUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-sm font-medium underline"
              >
                Continue to the registration site
              </a>
            ) : (
              <a
                href={`mailto:${organiserContact.email}`}
                className="inline-block text-sm font-medium underline"
              >
                Contact {organiserContact.name}
              </a>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // An event with no registerable division can't complete a registration —
  // show a clear notice instead of a form that dead-ends. Only when the read
  // definitively succeeded and returned nothing; a failed/filtered read must
  // never present as "closed".
  if (divisionGuard.confirmedNoDivisions) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 space-y-3 text-center">
            <h1 className="text-xl font-bold">
              Registration isn't open for this event yet
            </h1>
            <p className="text-sm text-muted-foreground">
              {event.name} doesn't have any divisions set up yet, so we can't
              take registrations. Please check back soon or contact{" "}
              {organiserContact.name}.
            </p>
            <a
              href={`mailto:${organiserContact.email}`}
              className="inline-block text-sm font-medium underline"
            >
              {organiserContact.email}
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  const cartLineItems = cart.items.filter(isItemComplete);
  const cartSubtotal = cartLineItems.reduce((s, i) => s + itemTotal(i), 0);
  const couponDiscount = cart.coupon_applied?.discount_amount ?? 0;
  // Entry fees only — the basis for automatic multi-team discounts.
  const cartEntryFees = cartLineItems.reduce(
    (s, i) => s + Number(i.base_price || 0),
    0
  );
  // An override coupon (e.g. RLCircuit26) replaces automatic policies.
  const couponOverrides = cart.coupon_applied?.overrides_policies === true &&
    couponDiscount > 0;
  const autoDiscount = (() => {
    if (couponOverrides) return null;
    const teamCount = cartLineItems.length;
    if (!teamCount || cartEntryFees <= 0) return null;
    const result = evaluateDiscountPolicies(
      (discountPolicies ?? []) as any[],
      teamCount,
      cartEntryFees,
      cartLineItems.map((i) => Number(i.base_price || 0))
    );
    if (!result) return null;
    const perTeam: Record<string, number> = {};
    cartLineItems.forEach((i, idx) => {
      const v = Number(result.perTeam?.[idx] ?? 0);
      if (v > 0) perTeam[i.client_id] = v;
    });
    return { amount: result.amount, label: result.label, perTeam };
  })();
  const autoDiscountAmount = autoDiscount?.amount ?? 0;
  const teamDiscount = (clientId: string) =>
    Number(autoDiscount?.perTeam?.[clientId] ?? 0) +
    Number(cart.coupon_applied?.per_team?.[clientId] ?? 0);

  const cartTotal = Math.max(
    0,
    cartSubtotal - couponDiscount - autoDiscountAmount
  );
  // Payment is a single choice for the entire cart — no online/later split.
  const paymentMethod = cart.payment_method_chosen ?? "online";
  const payingLater = paymentMethod !== "online";
  // 3% card processing fee. Card payments only — never check, cash or venmo.
  const cardFee = payingLater
    ? 0
    : Math.round(cartTotal * CARD_FEE_RATE * 100) / 100;
  const cartTotalWithFee = Math.round((cartTotal + cardFee) * 100) / 100;
  const onlineTotal = payingLater ? 0 : cartTotalWithFee;
  const laterTotal = payingLater ? cartTotal : 0;


  const showPaymentStep = !!event.allow_pay_later;
  const needsAccountStep = !user;
  const showPoolStep = !!event.registration_levels_enabled && (levels?.length ?? 0) > 0;
  // D32 — merged Scheduling step (tier + requests). Show whenever the event
  // has tiers enabled OR has any constraint offerings. We keep it always-on
  // for safety; if neither tiers nor offerings exist the inner step renders
  // a "no scheduling options" message.
  const showSchedulingStep = true;

  const steps: { id: StepId; label: string; show: boolean }[] = [
    { id: "team", label: "Team", show: true },
    { id: "coach", label: "Your Contact Details", show: true },
    { id: "scheduling", label: "Scheduling", show: showSchedulingStep },
    { id: "extras", label: "Extras", show: true },
    { id: "pool", label: "Pool", show: showPoolStep },
    { id: "payment", label: "Payment", show: showPaymentStep },
    { id: "account", label: "Account", show: needsAccountStep },
    { id: "review", label: "Review", show: true },
  ];
  const visibleSteps = steps.filter((s) => s.show);
  const stepIdx = visibleSteps.findIndex((s) => s.id === step);



  const goNext = () => {
    const missingZip = cart?.items.filter(isItemComplete).find((i) => !isValidHomeZip(i.team_home_zip));
    if (missingZip) {
      toast.error("Enter a 5-digit Team home ZIP for every team before continuing.");
      setEditingClientId(missingZip.client_id);
      setStep("team");
      return;
    }
    // Coach step: require a registrant phone and a coach phone for every team.
    if (step === "coach" && cart) {
      const items = cart.items.filter(isItemComplete);
      if (!cart.registrant_phone || phoneDigits(cart.registrant_phone).length < 10) {
        toast.error("Please enter your phone number before continuing.");
        return;
      }
      const missingCoachPhone = items.find(
        (i) =>
          !i.registrant_is_coach &&
          (!i.coach_phone || phoneDigits(i.coach_phone).length < 10)
      );
      if (missingCoachPhone) {
        toast.error(
          `Please enter a coach phone number for ${missingCoachPhone.team_display_name || missingCoachPhone.team_name}.`
        );
        return;
      }
    }

    // Extras step: block continuing when a required conditional field is empty
    // on ANY team in the cart (extras are per-team).
    if (step === "extras" && cart) {
      const incompleteTeam = cart.items.filter(isItemComplete).find((it) =>
        (it.extras ?? []).some((e) => {
          if (!e.qty || e.qty <= 0) return false;
          if (e.requires_player && (!e.player_name || !e.player_jersey_number))
            return true;
          if (e.requires_game_date && !e.game_date) return true;
          return false;
        })
      );
      if (incompleteTeam) {
        toast.error(
          `Complete the required extra details for ${
            incompleteTeam.team_display_name || incompleteTeam.team_name
          } before continuing.`
        );
        return;
      }
    }

    // D39 — Scheduling step: auto-commit complete drafts + gate paid tiers,
    // evaluated independently for every team in the cart.
    if (step === "scheduling" && cart) {
      const items = cart.items.filter(isItemComplete);
      const patches: Record<string, Partial<CartItem>> = {};
      const gates: Record<string, string> = {};

      for (const it of items) {
        const d = it.scheduling_draft ?? null;
        const specs: InputFieldSpec[] = (d?.input_field_specs ?? []) as any;
        const params = d?.params ?? {};
        const isFilled = (spec: InputFieldSpec) => {
          if (spec.required === false) return true;
          const v = params[spec.field_key];
          if (spec.input_type === "team_multiselect" || spec.input_type === "team_search_multiselect") {
            return Array.isArray(v) && v.length > 0;
          }
          return v !== undefined && v !== null && String(v).trim() !== "";
        };
        const draftComplete = !!d && specs.every(isFilled);
        const touched = !!d && Object.keys(params).some((k) => {
          const v = params[k];
          if (Array.isArray(v)) return v.length > 0;
          return v !== undefined && v !== null && String(v).trim() !== "";
        });
        const hasRequired = !!d && specs.some((s) => s.required !== false);

        let patch: Partial<CartItem> | null = null;
        if (d && draftComplete) {
          patch = {
            requested_constraints: [
              ...it.requested_constraints,
              {
                offering_id: d.offering_id,
                template_id: d.template_id,
                template_label: d.template_label,
                fee: d.fee,
                requires_review: d.requires_review,
                severity: d.severity,
                constraint_type: d.constraint_type,
                required_fields: specs.map((s) => s.field_key),
                input_field_specs: specs,
                params: { ...params },
              } as any,
            ],
            scheduling_draft: null,
          };
        } else if (d && (touched || hasRequired)) {
          gates[it.client_id] = "Finish or clear this team's scheduling request before continuing.";
          continue;
        } else if (d) {
          patch = { scheduling_draft: null };
        }

        if (patch) patches[it.client_id] = patch;

        const projectedConstraints =
          patch?.requested_constraints ?? it.requested_constraints ?? [];
        const tier = it.scheduling_tier ?? "standard";
        if (
          (tier === "priority" || tier === "club_priority") &&
          projectedConstraints.length === 0 &&
          !confirmedEmptyTiers.includes(it.client_id)
        ) {
          const tierName =
            tier === "priority"
              ? (event?.priority_tier_name || "").trim() || "Priority"
              : (event?.club_priority_tier_name || "").trim() || "Club Priority";
          gates[it.client_id] = `You've selected ${tierName} for this team but haven't added any requests yet. Add at least one request so we know what to prioritize — or continue without any.`;
        }
      }

      // Commit any draft-clearing / draft-commit patches regardless, so the
      // warning state stays accurate.
      if (Object.keys(patches).length > 0) {
        setCart({
          ...cart,
          items: cart.items.map((x) =>
            patches[x.client_id] ? { ...x, ...patches[x.client_id] } : x
          ),
        });
      }

      setSchedulingGate(gates);
      if (Object.keys(gates).length > 0) {
        toast.error("Some teams still need attention in the scheduling step.");
        return;
      }
    } else {
      setSchedulingGate({});
    }


    // Leaving the team step: discard the untouched trailing placeholder so it
    // can't leak into later steps, cart lines, or totals.
    //
    // NOTE: this must NOT call setEditingClientId from inside a setCart
    // updater — updaters are re-run during render, and a render-phase state
    // update there aborts the click's render pass, so setStep never landed and
    // Next appeared to do nothing.
    if (step === "team" && cart) {
      const kept = cart.items.filter((i) => !isBlankItem(i));
      if (!kept.some((i) => i.client_id === editingClientId)) {
        const fallback = kept[kept.length - 1];
        if (fallback) setEditingClientId(fallback.client_id);
      }
      if (kept.length !== cart.items.length) {
        const next = { ...cart, items: kept };
        saveCart(next);
        setCart(next);
      }
    }



    if (stepIdx < visibleSteps.length - 1) setStep(visibleSteps[stepIdx + 1].id);
  };
  // Coach explicitly confirms one team should proceed on a paid tier with no
  // scheduling requests added.
  const confirmEmptyForItem = (clientId: string) => {
    setConfirmedEmptyTiers((prev) =>
      prev.includes(clientId) ? prev : [...prev, clientId]
    );
    setSchedulingGate((prev) => {
      const next = { ...prev };
      delete next[clientId];
      return next;
    });
    toast.success("Noted — click Next to continue.");
  };
  const goBack = () => {
    setSchedulingGate({});
    if (stepIdx > 0) setStep(visibleSteps[stepIdx - 1].id);
  };


  // Gating ONLY ever looks at completed teams — placeholders are invisible here.
  const gatedItems = (cart?.items ?? []).filter(isItemComplete);
  const nextDisabled =
    !cart ||
    (step === "team" && (gatedItems.length === 0 || gatedItems.some((i) => !isValidHomeZip(i.team_home_zip)))) ||
    (step === "coach" &&
      (!cart.registrant_phone || phoneDigits(cart.registrant_phone).length < 10 ||
        gatedItems.some(
          (i) =>
            !i.registrant_is_coach &&
            (!i.coach_phone || phoneDigits(i.coach_phone).length < 10)
        ))) ||
    (step === "scheduling" &&
      !gatedItems.every((i) => constraintsAreValidForItem(i))) ||
    (step === "pool" &&
      !!event?.registration_levels_required &&
      !gatedItems.every((i) => !!i.pool));
  const showNext = step !== "review" && step !== "account";

  // Cart contents, shared between the desktop sidebar and the mobile sheet.
  const cartPanel = (
    <>
      <h3 className="font-semibold mb-1">Your cart</h3>
      {user ? (
        <p className="text-xs text-muted-foreground mb-2">
          Signed in as {user.email}
        </p>
      ) : (
        <p className="text-xs text-amber-700 mb-2">
          Not signed in — you'll create an account at checkout
        </p>
      )}
      <div className="space-y-3">
        {cartLineItems.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No teams yet. Finish a team to add it to your cart.
          </p>
        )}
        {cartLineItems.map((i) => (
          <div
            key={i.client_id}
            className="p-3 rounded border border-border text-sm"
          >
            <div className="flex justify-between items-start gap-2">
              <div>
                <div className="font-medium">
                  {i.team_display_name || i.team_name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {i.division_label} - {money(i.base_price)}
                </div>
                {i.requested_constraints?.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {i.requested_constraints.map((c: any, ci) => {
                      const detail = formatConstraintDetail(
                        c,
                        cart,
                        eventTeams ?? []
                      );
                      const waives = tierWaivesFees(i);
                      const fee = constraintFeeForDisplay(
                        c,
                        event?.constraint_type_pricing
                      );
                      return (
                        <li
                          key={`${c.template_id}-${ci}`}
                          className="text-[11px] text-muted-foreground"
                        >
                          Request: {c.template_label}
                          {detail && ` → ${detail}`}{" "}
                          {waives ? "(Included)" : `+${money(fee)}`}
                          {c.requires_review ? ", requires review" : ""}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {i.scheduling_tier && i.scheduling_tier !== "standard" && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Scheduling:{" "}
                    {(i.scheduling_tier === "priority" &&
                      (event?.priority_tier_name || "").trim()) ||
                      (i.scheduling_tier === "club_priority" &&
                        (event?.club_priority_tier_name || "").trim()) ||
                      (i.scheduling_tier === "showcase" &&
                        (event?.showcase_tier_name || "").trim()) ||
                      TIER_LABEL[i.scheduling_tier]}{" "}
                    +{money(Number(i.scheduling_tier_fee || 0))}
                  </div>
                )}
                {(i.extras ?? []).filter((e: any) => Number(e.qty || 0) > 0)
                  .length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {(i.extras ?? [])
                      .filter((e: any) => Number(e.qty || 0) > 0)
                      .map((e: any, ei: number) => (
                        <li
                          key={`${e.extra_id}-${ei}`}
                          className="text-[11px] text-muted-foreground"
                        >
                          Extra: {e.name}
                          {Number(e.qty) > 1 ? ` ×${e.qty}` : ""} +
                          {money(Number(e.price || 0) * Number(e.qty || 0))}
                        </li>
                      ))}
                  </ul>
                )}
              </div>

              <div className="text-right">
                {teamDiscount(i.client_id) > 0 && (
                  <div className="text-[11px] text-green-700">
                    Discount −{money(teamDiscount(i.client_id))}
                  </div>
                )}
                <div className="font-medium">
                  {money(Math.max(0, itemTotal(i) - teamDiscount(i.client_id)))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-border mt-4 pt-3 space-y-1 text-sm">
        {(couponDiscount > 0 || autoDiscountAmount > 0) && (
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Subtotal</span>
            <span>{money(cartSubtotal)}</span>
          </div>
        )}
        {couponDiscount > 0 && (
          <div className="flex justify-between text-xs text-green-700">
            <span>
              Coupon ({cart?.coupon_applied?.code})
              {cart?.coupon_applied?.fixed_scope === "per_team"
                ? ` — ${money(cart.coupon_applied.discount_value)} off each team's entry fee`
                : ""}
            </span>
            <span>−{money(couponDiscount)}</span>
          </div>
        )}
        {autoDiscountAmount > 0 && (
          <div className="flex justify-between text-xs text-green-700">
            <span>{autoDiscount!.label}</span>
            <span>−{money(autoDiscountAmount)}</span>
          </div>
        )}
        {cardFee > 0 && (
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Card processing fee (3%)</span>
            <span>{money(cardFee)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold">
          <span>Total</span>
          <span>{money(payingLater ? cartTotal : cartTotalWithFee)}</span>
        </div>
        {cartTotal > 0 && (
          <div className="text-xs text-muted-foreground">
            {payingLater
              ? `Pay later via ${paymentMethod}`
              : "Paying online at checkout"}
          </div>
        )}
      </div>

      {showNext && (
        <Button
          className="w-full mt-4 h-11"
          onClick={() => {
            setMobileCartOpen(false);
            goNext();
          }}
          disabled={nextDisabled}
        >
          Next
        </Button>
      )}
    </>
  );


  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-7xl mx-auto py-6 px-4 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Main wizard */}
        <div>
          <div className="mb-4">
            <h1 className="text-2xl font-bold">Register for {event.name}</h1>
            <p className="text-sm text-muted-foreground">
              Editing: {editingItem?.team_name || "New team"}
            </p>
          </div>

          {/* Step indicator — compact on narrow screens */}
          <div className="mb-6 lg:hidden">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium">
                Step {stepIdx + 1} of {visibleSteps.length} ·{" "}
                {visibleSteps[stepIdx]?.label}
              </p>
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{
                  width: `${((stepIdx + 1) / Math.max(1, visibleSteps.length)) * 100}%`,
                }}
              />
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-2 mb-6 flex-wrap">
            {visibleSteps.map((s, i) => (
              <button
                key={s.id}
                onClick={() => {
                  const missingZip = gatedItems.find((item) => !isValidHomeZip(item.team_home_zip));
                  if (s.id !== "team" && missingZip) {
                    toast.error("Enter a 5-digit Team home ZIP for every team before continuing.");
                    setEditingClientId(missingZip.client_id);
                    setStep("team");
                    return;
                  }
                  setStep(s.id);
                }}
                className={`px-3 py-1.5 text-xs rounded-full border whitespace-nowrap ${
                  step === s.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : i < stepIdx
                    ? "bg-primary/10 border-primary/30"
                    : "bg-background border-border"
                }`}
              >
                {i + 1}. {s.label}
              </button>
            ))}
          </div>


          <Card>
            <CardContent className="pt-6">
              {step === "team" && cart && (
                <TeamStep
                  cart={cart}
                  eventId={event.id}
                  eventStartDate={event.start_date}

                  editingClientId={editingClientId}
                  setEditingClientId={setEditingClientId}
                  updateItemById={(id, patch) =>
                    setCart((c) =>
                      c
                        ? {
                            ...c,
                            items: c.items.map((i) =>
                              i.client_id === id ? { ...i, ...patch } : i
                            ),
                          }
                        : c
                    )
                  }
                  onAddTeam={addAnotherTeam}
                  onCommitTeam={commitTeam}
                  onRemoveTeam={removeItem}
                  onContinue={goNext}
                />
              )}
              {step === "coach" &&
                (cart && !authLoading ? (
                  <CoachStep cart={cart} setCart={setCart} />
                ) : (
                  <CoachStepSkeleton />
                ))}

              {step === "scheduling" && cart && (
                <SchedulingStep
                  cart={cart}
                  setCart={setCart}
                  event={event}
                  eventStartDate={event.start_date}
                  eventEndDate={event.end_date}
                  eventTeams={eventTeams ?? []}
                  gateMessages={schedulingGate}
                  onContinueWithoutRequests={confirmEmptyForItem}
                />
              )}

              {step === "extras" && cart && (
                <ExtrasStep
                  cart={cart}
                  setCart={setCart}
                  eventId={event.id}
                  eventStartDate={event.start_date}
                  eventEndDate={event.end_date}
                />
              )}

              {step === "pool" && cart && (
                <PoolStep
                  cart={cart}
                  setCart={setCart}
                  levels={levels ?? []}
                  helpText={event.registration_levels_help_text}
                  required={!!event.registration_levels_required}
                />
              )}
              {step === "payment" && cart && (
                <PaymentStep cart={cart} setCart={setCart} event={event} />
              )}

              {step === "account" && cart && (
                <AccountStep
                  cart={cart}
                  setCart={setCart}
                  onAuthed={() => setStep("review")}
                />
              )}
              {step === "review" && (
                <>
                  {submitError && (
                    <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                      <p className="font-semibold">We couldn't submit your registration</p>
                      <p className="mt-1">{submitError}</p>
                      <p className="mt-1">
                        Nothing was charged. Your teams are still saved here — try again.
                      </p>
                    </div>
                  )}
                  <ReviewStep
                    cart={cart}
                    setCart={setCart}
                    onAddTeam={addAnotherTeam}
                    onEditTeamZip={(clientId) => {
                      setEditingClientId(clientId);
                      setStep("team");
                      window.setTimeout(() => {
                        const field = document.getElementById(`team-zip-${clientId}`);
                        field?.scrollIntoView({ block: "center", behavior: "auto" });
                        field?.focus();
                      }, 0);
                    }}
                    onSubmit={() => {
                      setSubmitError(null);
                      submit();
                    }}
                    submitting={submitting}
                    onlineTotal={onlineTotal}
                    laterTotal={laterTotal}
                    autoDiscount={autoDiscount}
                    event={event}

                  />
                </>
              )}
            </CardContent>
          </Card>

           <div className="flex justify-between mt-4">
             {stepIdx > 0 ? (
               <Button variant="outline" onClick={goBack}>
                 Back
               </Button>
             ) : (
               <span />
             )}
             {showNext && (
               <Button onClick={goNext} disabled={nextDisabled}>
                 Next
               </Button>
             )}
           </div>

        </div>

        {/* Cart sidebar (desktop) */}
        <aside className="hidden lg:block lg:sticky lg:top-6 self-start">
          <Card>
            <CardContent className="pt-6">{cartPanel}</CardContent>
          </Card>
        </aside>
      </div>

      {/* Spacer so the sticky mobile cart bar never covers page content */}
      <div className="h-24 lg:hidden" />

      {/* Sticky mobile cart */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background shadow-[0_-4px_16px_rgba(0,0,0,0.12)]">
        {mobileCartOpen && (
          <div className="max-h-[65vh] overflow-y-auto p-4 border-b border-border">
            {cartPanel}
          </div>
        )}
        <button
          type="button"
          onClick={() => setMobileCartOpen((o) => !o)}
          aria-expanded={mobileCartOpen}
          className="w-full min-h-[56px] px-4 py-3 flex items-center justify-between gap-3 text-left"
        >
          <span className="text-sm font-medium">
            {cartLineItems.length} team{cartLineItems.length === 1 ? "" : "s"}
            {" · "}
            <span className="font-semibold">
              {money(payingLater ? cartTotal : cartTotalWithFee)}
            </span>
          </span>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            {mobileCartOpen ? "Hide cart" : "View cart"}
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                mobileCartOpen ? "" : "rotate-180"
              }`}
            />
          </span>
        </button>
      </div>
    </div>
  );

}

// =================== STEP 1: Team ===================


function TeamStep({
  cart,
  eventId,
  eventStartDate,
  editingClientId,
  setEditingClientId,
  updateItemById,
  onAddTeam,
  onCommitTeam,
  onRemoveTeam,
  onContinue,
}: {
  cart: Cart;
  eventId: string;
  eventStartDate?: string | null;
  editingClientId: string | null;
  setEditingClientId: (id: string) => void;
  updateItemById: (id: string, patch: Partial<CartItem>) => void;
  onAddTeam: () => void;
  onCommitTeam: (id: string, patch: Partial<CartItem>) => void;
  onRemoveTeam: (id: string) => void;
  onContinue: () => void;
}) {
  const { data: divisions } = useQuery({
    queryKey: ["event-divisions", eventId],
    queryFn: async () => {
      const { data } = await sb
        .from("event_divisions")
        .select("id, name, age_group, gender, price_override, sort_order")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      return data ?? [];
    },
  });

  // Divisions configured on the event editor's Divisions step live in
  // event_division_settings (catalog division + boys/girls toggles + prices).
  // event_divisions is only populated by the AI builder, so fall back here.
  const { data: divisionSettings } = useQuery({
    queryKey: ["event-division-settings-public", eventId],
    queryFn: async () => {
      const { data } = await (sb as any)
        .from("event_division_settings")
        .select(
          "catalog_division_id, has_boys, has_girls, boys_price, girls_price, is_active, display_order, division_catalog:catalog_division_id(label, is_active)"
        )
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      return data ?? [];
    },
  });

  // Clubs already in the system — the parser matches against these first so
  // "Arizona Unity 16U EYBL" and "Arizona Unity EYBL 17U" land on one club.
  const { data: clubs } = useQuery({
    queryKey: ["clubs-for-team-parse"],
    queryFn: async () => {
      const { data } = await sb.from("clubs").select("id,name").limit(2000);
      return (data ?? []) as Array<{ id: string; name: string }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Teams that already hold an active registration for this event. A team can
  // only be registered once per event (erv2_one_active_per_team_per_event), so
  // catch it here instead of letting the insert fail at checkout.
  const { data: registeredTeams } = useQuery({
    queryKey: ["event-registered-team-ids", eventId],
    queryFn: async () => {
      const { data, error } = await (sb as any).rpc("event_registered_team_ids", {
        _event_id: eventId,
      });
      if (error) {
        console.error("event_registered_team_ids failed", error);
        return new Map<string, { name: string; registration_id: string }>();
      }
      const m = new Map<string, { name: string; registration_id: string }>();
      for (const r of (data ?? []) as any[]) {
        if (r.team_id)
          m.set(r.team_id, { name: r.team_name, registration_id: r.registration_id });
      }
      return m;
    },
    staleTime: 30 * 1000,
  });


  const [showInput, setShowInput] = useState(false);

  const options: DivisionOption[] = useMemo(() => {
    const out: DivisionOption[] = [];
    for (const d of divisions ?? []) {
      out.push({
        catalog_division_id: d.id,
        gender: (d.gender === "girls" || d.gender === "Girls" || d.gender === "female" ? "girls" : "boys") as "boys" | "girls",
        label: d.name,
        ageGroup: (d.age_group ?? d.name ?? "").toString(),
        price: Number(d.price_override) || 0,
      });
    }
    if (out.length > 0) return out;

    for (const s of (divisionSettings ?? []) as any[]) {
      const label = s.division_catalog?.label;
      if (!label) continue;
      if (s.has_boys) {
        out.push({
          catalog_division_id: s.catalog_division_id,
          gender: "boys",
          label: `Boys ${label}`,
          ageGroup: label,
          price: Number(s.boys_price) || 0,
        });
      }
      if (s.has_girls) {
        out.push({
          catalog_division_id: s.catalog_division_id,
          gender: "girls",
          label: `Girls ${label}`,
          ageGroup: label,
          price: Number(s.girls_price) || 0,
        });
      }
    }
    return out;
  }, [divisions, divisionSettings]);

  const optionKey = (o: DivisionOption) => `${o.catalog_division_id}:${o.gender}`;

  const activeItem =
    cart.items.find((i) => i.client_id === editingClientId) ??
    cart.items[cart.items.length - 1];

  const completedItems = cart.items.filter(
    (i) => i.client_id !== activeItem?.client_id && isItemComplete(i)
  );

  // Collapse the input again whenever the active blank slot changes (e.g. after
  // a team is committed and a fresh placeholder is appended).
  useEffect(() => {
    setShowInput(false);
  }, [activeItem?.client_id]);

  if (!activeItem) return null;

  const deleteTeam = (i: CartItem) => {
    if (
      Number(i.base_price || 0) > 0 &&
      !window.confirm(
        `Remove ${i.team_display_name || i.team_name} from your registration? This also removes its ${money(itemTotal(i))} line from the cart.`
      )
    ) {
      return;
    }
    onRemoveTeam(i.client_id);
  };

  // Once a team is in the cart we don't drop the coach back into a bare input —
  // we ask explicitly whether another team is coming.
  const isBlankActive =
    !!activeItem &&
    !isItemComplete(activeItem) &&
    !((activeItem as any).raw_team_input ?? "").trim() &&
    !(activeItem.team_display_name ?? "").trim() &&
    !(activeItem.team_name ?? "").trim();
  const askAnother = completedItems.length > 0 && isBlankActive && !showInput;
  const lastAdded = completedItems[completedItems.length - 1];

  const eventYear = eventStartDate
    ? new Date(eventStartDate).getFullYear()
    : new Date().getFullYear();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">What team are you registering?</h2>
      </div>


      {/* Confirmed teams */}
      {completedItems.length > 0 && (
        <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Added to your cart.
        </p>
        <div className="flex flex-wrap items-center gap-2">

          {completedItems.map((i) => (
            <div
              key={i.client_id}
              className="flex items-center gap-1 rounded-full border bg-muted/40 pl-3 pr-1 py-1 text-sm"
            >
              <span className="truncate max-w-[240px]">
                <strong>{i.team_display_name || i.team_name}</strong>
                {i.division_label ? ` · ${i.division_label}` : ""}
              </span>
              <button
                type="button"
                aria-label={`Edit ${i.team_display_name || i.team_name}`}
                title="Edit this team"
                className="p-1 rounded-full hover:bg-background"
                onClick={() => setEditingClientId(i.client_id)}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                aria-label={`Delete ${i.team_display_name || i.team_name}`}
                title="Remove this team"
                className="p-1 rounded-full hover:bg-background text-destructive"
                onClick={() => deleteTeam(i)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        </div>
      )}


      {askAnother ? (
        <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
          <p className="text-base font-semibold">
            {lastAdded
              ? `${lastAdded.team_display_name || lastAdded.team_name}${
                  lastAdded.division_label ? ` · ${lastAdded.division_label}` : ""
                } added.`
              : "Team added."}
          </p>
          <p className="text-sm text-muted-foreground">
            Registering another team?
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              size="lg"
              className="sm:w-auto w-full"
              onClick={() => setShowInput(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add another team
            </Button>
            <Button size="lg" className="sm:w-auto w-full" onClick={onContinue}>
              No, continue
            </Button>
          </div>
        </div>
      ) : (
      <TeamNameEntry
        key={activeItem.client_id}
        item={activeItem}
        options={options}
        optionKey={optionKey}
        clubs={clubs ?? []}
        registeredTeams={registeredTeams}
        eventYear={eventYear}
        updateItem={(patch) => updateItemById(activeItem.client_id, patch)}
        onCommit={(patch) => onCommitTeam(activeItem.client_id, patch)}
        onDiscard={() => deleteTeam(activeItem)}
      />
      )}
    </div>
  );
}

// Stored team name is club + variation only. The age group lives in its own
// field and is only ever added back for display.




// =================== STEP 2: Coach ===================

function CoachStepSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <div>
        <Skeleton className="h-6 w-48 mb-2" />
        <Skeleton className="h-4 w-64 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      </div>
      <div>
        <Skeleton className="h-5 w-40 mb-2" />
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  );
}

type CoachOnRecord = { name: string; email: string; phone: string };

function useCoachesOnRecord(teamIds: string[]) {
  const key = [...teamIds].sort().join(",");
  return useQuery({
    queryKey: ["cart-coaches-on-record", key],
    enabled: teamIds.length > 0,
    queryFn: async () => {
      const map: Record<string, CoachOnRecord> = {};

      const { data: teams } = await (sb as any)
        .from("teams")
        .select("id, primary_coach_name, primary_coach_user_id")
        .in("id", teamIds);

      const coachUserIds = (teams ?? [])
        .map((t: any) => t.primary_coach_user_id)
        .filter(Boolean);
      let profileById: Record<string, any> = {};
      if (coachUserIds.length) {
        const { data: profs } = await (sb as any)
          .from("profiles")
          .select("user_id, name, first_name, last_name, email, phone")
          .in("user_id", coachUserIds);
        for (const p of profs ?? []) profileById[p.user_id] = p;
      }

      for (const t of teams ?? []) {
        const p = t.primary_coach_user_id ? profileById[t.primary_coach_user_id] : null;
        const name =
          t.primary_coach_name ||
          p?.name ||
          `${p?.first_name ?? ""} ${p?.last_name ?? ""}`.trim();
        const email = p?.email ?? "";
        if (name || email) map[t.id] = { name, email, phone: p?.phone ?? "" };
      }

      // Fall back to the most recent coach invitation per team.
      const { data: invites } = await (sb as any)
        .from("coach_invitations")
        .select("team_id, coach_name, coach_email, coach_phone, created_at")
        .in("team_id", teamIds)
        .order("created_at", { ascending: false });
      for (const inv of invites ?? []) {
        if (map[inv.team_id]?.email) continue;
        if (!inv.coach_email && !inv.coach_name) continue;
        map[inv.team_id] = {
          name: inv.coach_name ?? map[inv.team_id]?.name ?? "",
          email: inv.coach_email ?? "",
          phone: inv.coach_phone ?? "",
        };
      }

      return map;
    },
  });
}

function teamHeading(i: CartItem) {
  const label = i.division_label;
  const name = i.team_display_name || i.team_name || "Team";
  return label ? `${name} — ${label}` : name;
}

function TeamCoachBlock({
  item,
  cart,
  onRecord,
  onUpdate,
}: {
  item: CartItem;
  cart: Cart;
  onRecord?: CoachOnRecord;
  onUpdate: (p: Partial<CartItem>) => void;
}) {
  const [overrideRecord, setOverrideRecord] = useState(false);

  const registrantFullName = `${cart.registrant_first_name ?? ""} ${cart.registrant_last_name ?? ""}`.trim();
  const registrantEmail = cart.registrant_email ?? "";


  // Show the "on record" card only when we have a record, the registrant isn't
  // the coach, nothing has been entered yet, and they haven't chosen to change.
  const showRecordCard =
    !!onRecord &&
    !overrideRecord &&
    !item.registrant_is_coach &&
    !item.coach_email &&
    !item.coach_name;

  return (
    <div className="rounded-lg border border-border p-4 space-y-4">
      <h3 className="text-base font-semibold">{teamHeading(item)}</h3>

      <div>
        <p className="text-sm font-medium mb-2">Are you the coach for this team?</p>
        <RadioGroup
          value={item.registrant_is_coach ? "yes" : "no"}
          onValueChange={(v) => {
            const isCoach = v === "yes";
            setOverrideRecord(false);
            onUpdate({
              registrant_is_coach: isCoach,
              ...(isCoach
                ? {
                    coach_name: registrantFullName,
                    coach_email: registrantEmail,
                    coach_phone: cart.registrant_phone ?? "",
                  }
                : { coach_name: "", coach_email: "", coach_phone: "" }),
            });
          }}
        >
          <label className="flex items-center gap-3 p-3 min-h-[48px] border border-border rounded cursor-pointer">
            <RadioGroupItem value="yes" />
            <span className="text-sm">Yes, I'm the coach</span>
          </label>
          <label className="flex items-center gap-3 p-3 min-h-[48px] border border-border rounded cursor-pointer">
            <RadioGroupItem value="no" />
            <span className="text-sm">No, someone else is</span>
          </label>
        </RadioGroup>
      </div>

      {showRecordCard && (
        <div className="rounded border border-border bg-muted/40 p-3 space-y-2">
          <p className="text-sm">
            Coach: <span className="font-medium">{onRecord!.name || "—"}</span>
            {onRecord!.email ? ` · ${onRecord!.email}` : ""}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() =>
                onUpdate({
                  coach_name: onRecord!.name,
                  coach_email: onRecord!.email,
                  coach_phone: onRecord!.phone,
                })
              }
            >
              That's right
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOverrideRecord(true)}>
              Different coach this time
            </Button>
          </div>
        </div>
      )}

      {!item.registrant_is_coach && !showRecordCard && (
        <div className="space-y-3">
          <div>
            <Label>Coach name *</Label>
            <Input
              value={item.coach_name}
              onChange={(e) => onUpdate({ coach_name: e.target.value })}
            />
          </div>
          <div>
            <Label>Coach email *</Label>
            <Input
              type="email"
              value={item.coach_email}
              onChange={(e) => onUpdate({ coach_email: e.target.value })}
            />
          </div>
          <div>
            <Label>Coach phone *</Label>
            <Input
              type="tel"
              formatPhone
              placeholder="xxx-xxx-xxxx"
              value={formatPhoneDisplay(item.coach_phone ?? "")}
              onChange={(e) => onUpdate({ coach_phone: phoneDigits(e.target.value) })}
            />
          </div>

        </div>
      )}

    </div>
  );
}

function CoachStep({
  cart,
  setCart,
}: {
  item?: CartItem;
  cart: Cart;
  setCart: (updater: any) => void;
  onUpdate?: (p: Partial<CartItem>) => void;
}) {
  const { user } = useAuth();
  const teams = cart.items.filter(isItemComplete);
  const teamIds = teams.map((t) => t.team_id).filter(Boolean) as string[];
  const { data: coachesOnRecord } = useCoachesOnRecord(teamIds);

  const updateItemById = (clientId: string, patch: Partial<CartItem>) =>
    setCart((c: Cart | null) =>
      c
        ? { ...c, items: c.items.map((i) => (i.client_id === clientId ? { ...i, ...patch } : i)) }
        : c
    );

  const updateRegistrant = (patch: Partial<Cart>) =>
    setCart((c: Cart | null) => {
      if (!c) return c;
      const next = { ...c, ...patch };
      const fullName = `${next.registrant_first_name ?? ""} ${next.registrant_last_name ?? ""}`.trim();
      return {
        ...next,
        items: next.items.map((i) =>
          i.registrant_is_coach
            ? {
                ...i,
                coach_name: fullName,
                coach_email: next.registrant_email ?? "",
                coach_phone: next.registrant_phone ?? "",
              }
            : i
        ),
      };
    });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Your contact details</h2>
        {user ? (
          <p className="text-sm text-muted-foreground mb-3">
            Confirmation will go to <strong>{cart.registrant_email}</strong>.{" "}
            <a href="/account" className="underline" target="_blank" rel="noreferrer">
              Change it in account settings
            </a>
            .
          </p>
        ) : (
          <p className="text-sm text-muted-foreground mb-3">
            We'll send the order confirmation here and use this address for your
            account at the end.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <Label>First name *</Label>
            <Input
              value={cart.registrant_first_name ?? ""}
              onChange={(e) => updateRegistrant({ registrant_first_name: e.target.value })}
            />
          </div>
          <div>
            <Label>Last name *</Label>
            <Input
              value={cart.registrant_last_name ?? ""}
              onChange={(e) => updateRegistrant({ registrant_last_name: e.target.value })}
            />
          </div>
          {!user && (
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={cart.registrant_email ?? ""}
                onChange={(e) => updateRegistrant({ registrant_email: e.target.value })}
              />
            </div>
          )}
          <div>
            <Label>Phone *</Label>
            <Input
              type="tel"
              formatPhone
              placeholder="xxx-xxx-xxxx"
              value={formatPhoneDisplay(cart.registrant_phone ?? "")}
              onChange={(e) => updateRegistrant({ registrant_phone: phoneDigits(e.target.value) })}
            />

          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Coach for each team</h2>
          <p className="text-sm text-muted-foreground">
            Each team can have a different coach.
          </p>
        </div>
        {teams.map((t) => (
          <TeamCoachBlock
            key={t.client_id}
            item={t}
            cart={cart}
            onRecord={t.team_id ? coachesOnRecord?.[t.team_id] : undefined}
            onUpdate={(patch) => updateItemById(t.client_id, patch)}
          />
        ))}
      </div>

      <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-900 dark:text-blue-100">
        We'll email each coach a magic link to manage their roster. You'll keep access too.
      </div>
    </div>
  );
}


// =================== STEP 3: Scheduling (per team) ===================

function SchedulingStep({
  cart,
  setCart,
  event,
  eventStartDate,
  eventEndDate,
  eventTeams,
  gateMessages,
  onContinueWithoutRequests,
}: {
  cart: Cart;
  setCart: (updater: any) => void;
  event: any;
  eventStartDate?: string | null;
  eventEndDate?: string | null;
  eventTeams: EventTeamRow[];
  gateMessages?: Record<string, string>;
  onContinueWithoutRequests?: (clientId: string) => void;
}) {
  const teams = cart.items.filter(isItemComplete);

  const updateItemById = (clientId: string, patch: Partial<CartItem>) =>
    setCart((c: Cart | null) =>
      c
        ? { ...c, items: c.items.map((i) => (i.client_id === clientId ? { ...i, ...patch } : i)) }
        : c
    );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">
          Scheduling for your {teams.length}{" "}
          {teams.length === 1 ? "team" : "teams"}
        </h2>
        <p className="text-sm text-muted-foreground">
          For example, conflicts for your 17u don't have to apply to your 12u.
        </p>


      </div>

      {teams.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Add a team first to set up scheduling.
        </p>
      )}

      {teams.map((t) => (
        <TeamSchedulingBlock
          key={t.client_id}
          item={t}
          cart={cart}
          event={event}
          eventStartDate={eventStartDate}
          eventEndDate={eventEndDate}
          eventTeams={eventTeams}
          onUpdate={(patch) => updateItemById(t.client_id, patch)}
          gateMessage={gateMessages?.[t.client_id] ?? null}
          onContinueWithoutRequests={
            onContinueWithoutRequests
              ? () => onContinueWithoutRequests(t.client_id)
              : undefined
          }
        />
      ))}
    </div>
  );
}

function TeamSchedulingBlock({
  item,
  cart,
  event,
  eventStartDate,
  eventEndDate,
  eventTeams,
  onUpdate,
  gateMessage,
  onContinueWithoutRequests,
}: {
  item: CartItem;
  cart: Cart;
  event: any;
  eventStartDate?: string | null;
  eventEndDate?: string | null;
  eventTeams: EventTeamRow[];
  onUpdate: (patch: Partial<CartItem>) => void;
  gateMessage?: string | null;
  onContinueWithoutRequests?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const needsAttention = !!gateMessage || !!item.scheduling_draft;
  useEffect(() => {
    if (needsAttention) setOpen(true);
  }, [needsAttention]);

  const tiersEnabled = !!event.scheduling_tiers_enabled;

  const priorityPrice = Number(event.priority_tier_price ?? 0);
  const clubPrice = Number(event.club_priority_tier_price ?? 0);
  const showcasePrice = Number(event.showcase_tier_price ?? 0);
  const minClubTeams = Number(event.club_priority_min_teams ?? 5);
  const showcaseEnabled = !!event.showcase_tier_enabled && showcasePrice > 0;
  const hasPaidTiers =
    tiersEnabled && (priorityPrice > 0 || clubPrice > 0 || showcaseEnabled);

  const priorityName = (event.priority_tier_name || "").trim() || "Priority";
  const clubName = (event.club_priority_tier_name || "").trim() || "Club Priority";
  const showcaseName = (event.showcase_tier_name || "").trim() || "Showcase";
  const priorityDesc = (event.priority_tier_description || "").trim();
  const clubDesc = (event.club_priority_tier_description || "").trim();
  const showcaseDesc = (event.showcase_tier_description || "").trim();

  // Selected perk bullets per tier (fall back to defaults if none chosen).
  const priorityBullets = (() => {
    const sel = perkLabels(event.priority_tier_perks);
    return sel.length ? sel : perkLabels(DEFAULT_TIER_PERKS.priority);
  })();
  const clubBullets = (() => {
    const sel = perkLabels(event.club_priority_tier_perks);
    return sel.length ? sel : perkLabels(DEFAULT_TIER_PERKS.club_priority);
  })();
  const showcaseBullets = (() => {
    const sel = perkLabels(event.showcase_tier_perks);
    return sel.length ? sel : perkLabels(DEFAULT_TIER_PERKS.showcase);
  })();

  const tierWaiveConfig: Record<string, boolean> = {
    priority: event.priority_tier_waives_fees !== false,
    club_priority: event.club_priority_tier_waives_fees !== false,
    showcase: event.showcase_tier_waives_fees !== false,
  };

  const sameClubCount = item.club_id
    ? cart.items.filter(
        (o) => o.event_id === item.event_id && o.club_id && o.club_id === item.club_id
      ).length
    : 0;
  const clubEligible = sameClubCount >= minClubTeams;

  const current = item.scheduling_tier ?? "standard";

  const select = (tier: CartItem["scheduling_tier"], fee: number) => {
    onUpdate({
      scheduling_tier: tier,
      scheduling_tier_fee: fee,
      scheduling_tier_waives_fees: tier ? (tierWaiveConfig[tier] ?? false) : false,
    });
  };

  const TierCard = ({
    tier,
    title,
    priceLabel,
    description,
    bullets,
    disabled,
    helper,
    customNote,
    onSelect,
  }: {
    tier: string;
    title: string;
    priceLabel: string;
    description: string;
    bullets: string[];
    disabled?: boolean;
    helper?: string;
    customNote?: string;
    onSelect: () => void;
  }) => {
    const selected = current === tier;
    return (
      <button
        type="button"
        onClick={() => !disabled && onSelect()}
        disabled={disabled}
        className={`text-left p-4 rounded border transition w-full ${
          selected
            ? "border-primary bg-primary/5 ring-1 ring-primary"
            : "border-border bg-background hover:bg-muted/30"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary/60"}`}
      >
        <div className="flex justify-between items-start gap-2 mb-1">
          <div className="font-semibold">{title}</div>
          <div className="text-sm font-medium text-primary whitespace-nowrap">
            {priceLabel}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-2">{description}</p>
        <ul className="text-xs space-y-1 list-disc list-inside text-muted-foreground">
          {bullets.map((b, i) => {
            const isUpper = b === b.toUpperCase() && /[A-Z]/.test(b);
            return (
              <li key={i} className={isUpper ? "font-bold text-foreground" : ""}>{b}</li>
            );
          })}
        </ul>
        {customNote && (
          <p className="mt-2 text-xs text-foreground whitespace-pre-line border-t border-border pt-2">
            {customNote}
          </p>
        )}
        {helper && (
          <div className={`mt-2 text-[11px] ${disabled ? "text-amber-700" : "text-emerald-700"}`}>
            {helper}
          </div>
        )}
      </button>
    );
  };

  const priorityUpgrade = () => onUpdate({ scheduling_tier: "priority", scheduling_tier_fee: priorityPrice });

  const waives = tierWaivesFees(item);
  const constraintsTotal = waives
    ? 0
    : (item.requested_constraints ?? []).reduce((s, c) => s + (c.fee || 0), 0);
  const tierFee = Number(item.scheduling_tier_fee || 0);
  const schedulingTotal = constraintsTotal + tierFee;

  const requestCount = (item.requested_constraints ?? []).length;
  const summaryText =
    requestCount === 0
      ? "No scheduling requests"
      : `${requestCount} request${requestCount === 1 ? "" : "s"}${
          schedulingTotal > 0 ? ` · ${money(schedulingTotal)}` : ""
        }`;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{teamHeading(item)}</h3>
          <p className="text-xs text-muted-foreground">
            {summaryText} · Team total {money(itemTotal(item))}
          </p>
        </div>
        <Button
          type="button"
          variant={open ? "outline" : "default"}
          size="sm"
          className="shrink-0"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" /> Hide
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-1" /> Add a scheduling request
            </>
          )}
        </Button>
      </div>

      {open && (
      <>



      {hasPaidTiers && (
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Choose a scheduling tier (optional)
            </div>
            <p className="text-xs text-muted-foreground">
              Not selecting one keeps your team on standard entry. Individual requests can still be purchased below.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {priorityPrice > 0 && (
              <TierCard
                tier="priority"
                title={priorityName}
                priceLabel={`+${money(priorityPrice)} per team`}
                description="Your hard constraints are honored first."
                bullets={priorityBullets}
                onSelect={() => select("priority", priorityPrice)}
              />
            )}

            {clubPrice > 0 && (
              <TierCard
                tier="club_priority"
                title={clubName}
                priceLabel={`+${money(clubPrice)} per team`}
                description="Cross-team coordination for clubs with multiple teams."
                bullets={clubBullets}
                disabled={!clubEligible}
                helper={
                  clubEligible
                    ? `Eligible — you have ${sameClubCount} teams`
                    : `Requires ${minClubTeams}+ teams from the same club in cart (you have ${sameClubCount})`
                }
                onSelect={() => select("club_priority", clubPrice)}
              />
            )}

            {showcaseEnabled && (
              <TierCard
                tier="showcase"
                title={showcaseName}
                priceLabel={`+${money(showcasePrice)} per team`}
                description="Featured exposure for recruiting events."
                bullets={showcaseBullets}
                onSelect={() => select("showcase", showcasePrice)}
              />
            )}
          </div>
          {current !== "standard" && (
            <button
              type="button"
              className="text-xs underline text-muted-foreground"
              onClick={() => select("standard", 0)}
            >
              Keep this team on standard entry instead
            </button>
          )}
        </div>
      )}


      {hasPaidTiers && <div className="border-t" />}

      <ConstraintsStep
        item={item}
        eventId={event.id}
        eventStartDate={eventStartDate}
        eventEndDate={eventEndDate}
        cart={cart}
        eventTeams={eventTeams}
        constraintTypePricing={event.constraint_type_pricing ?? null}
        priorityTierPrice={priorityPrice}
        onUpdate={onUpdate}
        onUpgradeToPriority={priorityPrice > 0 ? priorityUpgrade : undefined}
        gateMessage={gateMessage}
        tierName={
          current === "priority"
            ? priorityName
            : current === "club_priority"
            ? clubName
            : current === "showcase"
            ? showcaseName
            : null
        }
        onContinueWithoutRequests={onContinueWithoutRequests}
      />

      <div className="border-t pt-3 space-y-1 text-sm">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Entry fee</span>
          <span>{money(item.base_price || 0)}</span>
        </div>
        {tierFee > 0 && (
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Scheduling tier</span>
            <span>+{money(tierFee)}</span>
          </div>
        )}
        {(item.requested_constraints?.length ?? 0) > 0 && (
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              Scheduling requests ({item.requested_constraints.length})
            </span>
            <span>{waives ? "Included" : `+${money(constraintsTotal)}`}</span>
          </div>
        )}
        {(item.extras ?? [])
          .filter((e: any) => Number(e.qty || 0) > 0)
          .map((e: any, ei: number) => (
            <div
              key={`x-${ei}`}
              className="flex justify-between text-xs text-muted-foreground"
            >
              <span>
                Extra: {e.name}
                {Number(e.qty) > 1 ? ` ×${e.qty}` : ""}
              </span>
              <span>+{money(Number(e.price || 0) * Number(e.qty || 0))}</span>
            </div>
          ))}

        <div className="flex justify-between font-semibold pt-1">
          <span>{item.team_display_name || item.team_name} total</span>
          <span>{money(itemTotal(item))}</span>
        </div>
      </div>
      </>
      )}
    </div>

  );
}


// =================== STEP 3: Constraints ===================

// =================== Constraint input specs (DB-driven) ===================

interface InputFieldSpec {
  field_key: string;
  label: string;
  input_type:
    | "date"
    | "time"
    | "team_select"
    | "team_multiselect"
    | "team_search_select"
    | "team_search_multiselect";
  required?: boolean;
  options_source?: "event_registered_teams" | "all_teams";
  help_text?: string;
  validation?: string;
}

// =================== Team search components (typeahead, all teams) ===================

type TeamSearchResult = {
  team_id: string;
  display_name: string;
  age_group: string | null;
  gender: string | null;
  club_name: string | null;
  home_state: string | null;
};

function formatTeamMeta(t: TeamSearchResult): string {
  const bits = [
    t.club_name,
    [t.age_group, t.gender].filter(Boolean).join(" "),
    t.home_state,
  ].filter((b) => b && String(b).trim() !== "");
  return bits.join(" · ");
}

function useTeamSearch(query: string, ageGroup?: string, gender?: string) {
  const [results, setResults] = useState<TeamSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      const { data, error } = await (sb as any).rpc("list_all_teams", {
        p_search: q,
        p_age_group: ageGroup ?? null,
        p_gender: gender ?? null,
        p_limit: 20,
      });
      if (cancelled) return;
      setLoading(false);
      if (error) {
        console.error("list_all_teams failed", error);
        setResults([]);
        return;
      }
      setResults((data ?? []) as TeamSearchResult[]);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, ageGroup, gender]);
  return { results, loading };
}

function TeamSearchSelect({
  value,
  displayValue,
  onChange,
  ageGroup,
  gender,
  helpText,
  cartOptions = [],
}: {
  value: string;
  displayValue?: string;
  onChange: (teamId: string, displayName: string) => void;
  ageGroup?: string;
  gender?: string;
  required?: boolean;
  helpText?: string;
  cartOptions?: Array<{ value: string; label: string }>;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const { results, loading } = useTeamSearch(query, ageGroup, gender);

  if (value) {
    return (
      <div className="flex items-center gap-2">
        <Input value={displayValue || "Selected team"} readOnly className="flex-1" />
        <Button type="button" variant="outline" size="sm" onClick={() => onChange("", "")}>
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      {cartOptions.length > 0 && (
        <div className="mb-2 space-y-1">
          <p className="text-[11px] text-muted-foreground">Teams in this registration</p>
          <div className="flex flex-wrap gap-1">
            {cartOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value, opt.label)}
                className="px-2 py-1 text-xs rounded border hover:bg-accent"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Input
        placeholder="Search for a team..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, results.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter" && results[highlight]) {
            e.preventDefault();
            const r = results[highlight];
            onChange(r.team_id, r.display_name);
            setQuery("");
            setOpen(false);
          }
        }}
      />
      {open && query.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto bg-popover border rounded-md shadow-lg">
          {loading && (
            <div className="p-2 text-xs text-muted-foreground">Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="p-2 text-xs text-muted-foreground">
              No teams found. Try a different search.
            </div>
          )}
          {!loading &&
            results.map((r, i) => (
              <button
                key={r.team_id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(r.team_id, r.display_name);
                  setQuery("");
                  setOpen(false);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-accent ${
                  i === highlight ? "bg-accent" : ""
                }`}
              >
                <div className="font-medium">{r.display_name}</div>
                <div className="text-xs text-muted-foreground">{formatTeamMeta(r)}</div>
              </button>
            ))}
        </div>
      )}
      {helpText && (
        <p className="text-[11px] text-muted-foreground mt-1">{helpText}</p>
      )}
    </div>
  );
}

function TeamSearchMultiSelect({
  values,
  displayValues,
  onChange,
  ageGroup,
  gender,
  helpText,
  cartOptions = [],
}: {
  values: string[];
  displayValues: Record<string, string>;
  onChange: (ids: string[], displays: Record<string, string>) => void;
  ageGroup?: string;
  gender?: string;
  required?: boolean;
  helpText?: string;
  cartOptions?: Array<{ value: string; label: string }>;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const { results, loading } = useTeamSearch(query, ageGroup, gender);

  const addTeam = (r: TeamSearchResult) => {
    if (values.includes(r.team_id)) return;
    onChange([...values, r.team_id], { ...displayValues, [r.team_id]: r.display_name });
  };

  const removeTeam = (id: string) => {
    const newDisplays = { ...displayValues };
    delete newDisplays[id];
    onChange(
      values.filter((v) => v !== id),
      newDisplays
    );
  };

  return (
    <div className="space-y-2">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-secondary"
            >
              {displayValues[id] || "Team"}
              <button
                type="button"
                onClick={() => removeTeam(id)}
                className="hover:text-destructive"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      {cartOptions.filter((o) => !values.includes(o.value)).length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] text-muted-foreground">Teams in this registration</p>
          <div className="flex flex-wrap gap-1">
            {cartOptions
              .filter((o) => !values.includes(o.value))
              .map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    onChange([...values, opt.value], {
                      ...displayValues,
                      [opt.value]: opt.label,
                    })
                  }
                  className="px-2 py-1 text-xs rounded border hover:bg-accent"
                >
                  + {opt.label}
                </button>
              ))}
          </div>
        </div>
      )}
      <div className="relative">

        <Input
          placeholder="Search for a team..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, results.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === "Enter" && results[highlight]) {
              e.preventDefault();
              addTeam(results[highlight]);
              setQuery("");
            }
          }}
        />
        {open && query.trim().length >= 2 && (
          <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto bg-popover border rounded-md shadow-lg">
            {loading && (
              <div className="p-2 text-xs text-muted-foreground">Searching…</div>
            )}
            {!loading && results.length === 0 && (
              <div className="p-2 text-xs text-muted-foreground">
                No teams found. Try a different search.
              </div>
            )}
            {!loading &&
              results.map((r, i) => {
                const already = values.includes(r.team_id);
                return (
                  <button
                    key={r.team_id}
                    type="button"
                    disabled={already}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (!already) {
                        addTeam(r);
                        setQuery("");
                      }
                    }}
                    onMouseEnter={() => setHighlight(i)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-accent disabled:opacity-50 ${
                      i === highlight ? "bg-accent" : ""
                    }`}
                  >
                    <div className="font-medium">
                      {r.display_name} {already && <span className="text-xs">(added)</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{formatTeamMeta(r)}</div>
                  </button>
                );
              })}
          </div>
        )}
      </div>
      {helpText && (
        <p className="text-[11px] text-muted-foreground">{helpText}</p>
      )}
    </div>
  );
}

interface ConstraintOfferingRow {
  id: string;
  override_fee: number | null;
  override_severity: string | null;
  override_requires_review: boolean | null;
  display_order: number | null;
  is_active: boolean;
  template_id: string;
  constraint_templates: {
    id: string;
    label: string;
    description: string | null;
    constraint_type: string;
    default_severity: string | null;
    requires_review: boolean | null;
    default_fee: number | null;
    input_field_specs: InputFieldSpec[] | null;
  } | null;
}

type EventTeamRow = { team_id: string; display_name: string };

function buildTeamOptions(
  cart: Cart | null,
  eventTeams: EventTeamRow[],
  currentClientId: string
): Array<{ value: string; label: string }> {
  const cartTeams = (cart?.items ?? [])
    .filter((t) => t.client_id !== currentClientId)
    .filter((t) => (t.team_display_name || t.team_name || "").trim() !== "")
    .map((t) => ({
      value: `cart:${t.client_id}`,
      label: `${t.team_display_name || t.team_name} (this registration)`,
    }));
  const registered = (eventTeams ?? []).map((t) => ({
    value: `team:${t.team_id}`,
    label: t.display_name,
  }));
  const seen = new Set<string>();
  return [...cartTeams, ...registered].filter((opt) => {
    const key = opt.label.toLowerCase().replace(/\s*\(this registration\)\s*$/, "");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function resolveTeamLabel(
  value: string,
  cart: Cart | null,
  eventTeams: EventTeamRow[]
): string {
  if (typeof value !== "string") return String(value ?? "");
  if (value.startsWith("cart:")) {
    const cid = value.slice(5);
    const t = (cart?.items ?? []).find((x) => x.client_id === cid);
    return t ? t.team_display_name || t.team_name || "Unknown team" : "Unknown team";
  }
  if (value.startsWith("team:")) {
    const id = value.slice(5);
    const t = (eventTeams ?? []).find((x) => x.team_id === id);
    return t ? t.display_name : "Unknown team";
  }
  return value;
}

function formatConstraintDetail(
  c: any,
  cart: Cart | null,
  eventTeams: EventTeamRow[]
): string {
  const specs: InputFieldSpec[] = c.input_field_specs ?? [];
  const params = c.params ?? {};
  const parts: string[] = [];
  // Group date+time pairs nicely
  const dateField = specs.find((s) => s.input_type === "date");
  const timeFields = specs.filter((s) => s.input_type === "time");
  if (dateField && timeFields.length > 0 && params[dateField.field_key]) {
    const d = params[dateField.field_key];
    const times = timeFields
      .map((tf) => params[tf.field_key])
      .filter(Boolean)
      .join(" – ");
    parts.push([d, times].filter(Boolean).join(" "));
  } else if (dateField && params[dateField.field_key]) {
    parts.push(String(params[dateField.field_key]));
  }
  for (const s of specs) {
    if (s.input_type === "team_select") {
      const v = params[s.field_key];
      if (v) parts.push(`${s.label}: ${resolveTeamLabel(String(v), cart, eventTeams)}`);
    } else if (s.input_type === "team_multiselect") {
      const arr = Array.isArray(params[s.field_key]) ? params[s.field_key] : [];
      if (arr.length > 0) {
        parts.push(
          `${s.label}: ${arr.map((v: string) => resolveTeamLabel(v, cart, eventTeams)).join(", ")}`
        );
      }
    } else if (s.input_type === "team_search_select") {
      const v = params[s.field_key];
      const disp = params[`${s.field_key}__display`];
      if (v) parts.push(`${s.label}: ${disp || v}`);
    } else if (s.input_type === "team_search_multiselect") {
      const arr = Array.isArray(params[s.field_key]) ? params[s.field_key] : [];
      const dispMap = (params[`${s.field_key}__display`] as Record<string, string>) || {};
      if (arr.length > 0) {
        parts.push(
          `${s.label}: ${arr.map((id: string) => dispMap[id] || id).join(", ")}`
        );
      }
    }
  }
  return parts.join(" · ");
}

function ConstraintsStep({
  item,
  eventId,
  eventStartDate,
  eventEndDate,
  cart,
  eventTeams,
  constraintTypePricing,
  priorityTierPrice,
  onUpdate,
  onUpgradeToPriority,
  gateMessage,
  tierName,
  onContinueWithoutRequests,
}: {
  item: CartItem;
  eventId: string;
  eventStartDate?: string | null;
  eventEndDate?: string | null;
  cart: Cart | null;
  eventTeams: EventTeamRow[];
  constraintTypePricing?: Record<string, any> | null;
  priorityTierPrice?: number;
  onUpdate: (p: Partial<CartItem>) => void;
  onUpgradeToPriority?: () => void;
  gateMessage?: string | null;
  tierName?: string | null;
  onContinueWithoutRequests?: () => void;
}) {
  const { data: offerings, isLoading } = useQuery({
    queryKey: ["event-constraint-offerings", eventId],
    queryFn: async () => {
      const { data, error } = await sb
        .from("event_constraint_offerings")
        .select(
          `
          id,
          override_fee,
          override_severity,
          override_requires_review,
          display_order,
          is_active,
          template_id,
          constraint_templates!template_id (
            id,
            label,
            description,
            constraint_type,
            default_severity,
            requires_review,
            default_fee,
            input_field_specs
          )
        `
        )
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as ConstraintOfferingRow[];
    },
  });

  // D39 — draft persists on the cart item, not in local state
  const draft = item.scheduling_draft ?? null;
  const selectedTemplateId = draft?.template_id ?? "";
  const draftParams = draft?.params ?? {};

  const setDraftParams = (
    updater: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)
  ) => {
    if (!item.scheduling_draft) return;
    const next =
      typeof updater === "function"
        ? (updater as any)(item.scheduling_draft.params ?? {})
        : updater;
    onUpdate({
      scheduling_draft: { ...item.scheduling_draft, params: next },
    });
  };

  const teamLabel = item.team_display_name || item.team_name || "this team";
  const teamOptions = buildTeamOptions(cart, eventTeams, item.client_id);
  const hasTeamOptions = teamOptions.length > 0;

  const effective = (o: ConstraintOfferingRow) => {
    const t = o.constraint_templates;
    const ct = t?.constraint_type ?? null;
    const overrides = constraintTypePricing ?? {};
    const eventOverride =
      ct && overrides[ct] !== undefined && overrides[ct] !== null
        ? Number(overrides[ct])
        : null;
    const fee =
      eventOverride ?? Number(o.override_fee ?? t?.default_fee ?? 0);
    return {
      offering_id: o.id,
      template_id: o.template_id,
      label: t?.label ?? "Request",
      description: t?.description ?? null,
      fee,
      severity: o.override_severity ?? t?.default_severity ?? null,
      requires_review: o.override_requires_review ?? t?.requires_review ?? false,
      constraint_type: ct,
      input_field_specs: (t?.input_field_specs ?? []) as InputFieldSpec[],
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading requests…
      </div>
    );
  }

  const tier = item.scheduling_tier ?? "standard";
  const tierWaives = tierWaivesFees(item);

  if (!offerings || offerings.length === 0) {
    return (
      <div>
        <h2 className="text-lg font-semibold mb-2">Scheduling requests</h2>
        <p className="text-sm text-muted-foreground">
          ✓ No scheduling requests configured for this event.
        </p>
      </div>
    );
  }

  // Show all offerings — no filtering. Coaches can flag conflicts against any team.
  const visibleOfferings = offerings;

  const selectedOffering = visibleOfferings.find(
    (o) => o.template_id === selectedTemplateId
  );
  // Prefer the draft's cached effective fields (so fee/specs are stable even
  // if offering data changes), fall back to recomputing from current offering.
  const selectedEff = draft
    ? {
        offering_id: draft.offering_id,
        template_id: draft.template_id,
        label: draft.template_label,
        description: selectedOffering
          ? effective(selectedOffering).description
          : null,
        fee: draft.fee,
        severity: draft.severity,
        requires_review: draft.requires_review,
        constraint_type: draft.constraint_type,
        input_field_specs: (draft.input_field_specs ?? []) as InputFieldSpec[],
      }
    : null;

  const requiredFilled =
    !selectedEff ||
    selectedEff.input_field_specs.every((spec) => {
      if (spec.required === false) return true;
      const v = draftParams[spec.field_key];
      if (spec.input_type === "team_multiselect" || spec.input_type === "team_search_multiselect") {
        return Array.isArray(v) && v.length > 0;
      }
      return v !== undefined && v !== null && String(v).trim() !== "";
    });

  const resetDraft = () => {
    onUpdate({ scheduling_draft: null });
  };

  const selectTemplate = (templateId: string) => {
    const o = visibleOfferings.find((x) => x.template_id === templateId);
    if (!o) return;
    const eff = effective(o);
    onUpdate({
      scheduling_draft: {
        offering_id: eff.offering_id,
        template_id: eff.template_id,
        template_label: eff.label,
        fee: eff.fee,
        requires_review: !!eff.requires_review,
        severity: eff.severity,
        constraint_type: eff.constraint_type,
        input_field_specs: eff.input_field_specs,
        params: {},
      },
    });
  };

  const addRequest = () => {
    if (!selectedEff) return;
    if (!requiredFilled) {
      toast.error("Please fill required fields");
      return;
    }
    onUpdate({
      requested_constraints: [
        ...item.requested_constraints,
        {
          offering_id: selectedEff.offering_id,
          template_id: selectedEff.template_id,
          template_label: selectedEff.label,
          fee: selectedEff.fee,
          requires_review: selectedEff.requires_review,
          severity: selectedEff.severity,
          constraint_type: selectedEff.constraint_type,
          required_fields: selectedEff.input_field_specs.map((s) => s.field_key),
          input_field_specs: selectedEff.input_field_specs,
          params: { ...draftParams },
        } as any,
      ],
      scheduling_draft: null,
    });
  };

  const removeRequest = (idx: number) => {
    onUpdate({
      requested_constraints: item.requested_constraints.filter((_, i) => i !== idx),
    });
  };

  const added = item.requested_constraints ?? [];

  const subhead =
    tier === "priority" || tier === "club_priority"
      ? "Add unlimited scheduling requests — all honored first, no extra cost. Your tier sets the priority; the requests below are the specific things we'll prioritize, so add at least one to get the value."
      : tier === "showcase"
      ? "Featured court placement is handled by the tournament director. Add any additional scheduling requests here if you have them (no extra cost)."
      : "Optional. Add specific scheduling requests à la carte — each priced individually.";

  const paidTier = tier === "priority" || tier === "club_priority";
  const showEmptyPaidWarning = paidTier && added.length === 0;

  const requestFeesSum = added.reduce(
    (s: number, c: any) => s + constraintFeeForDisplay(c, constraintTypePricing),
    0
  );
  const showUpsell =
    tier === "standard" &&
    added.length >= 3 &&
    !!priorityTierPrice &&
    requestFeesSum > Number(priorityTierPrice) &&
    !!onUpgradeToPriority;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Scheduling requests</h2>
        <p className="text-xs text-muted-foreground">{subhead}</p>
      </div>

      {gateMessage && (
        <div className="p-3 border border-destructive/40 bg-destructive/10 rounded text-sm text-destructive">
          {gateMessage}
        </div>
      )}

      {showEmptyPaidWarning && (
        <div className="p-4 border-2 border-amber-400 bg-amber-50 rounded-lg space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              You've selected{" "}
              <span className="font-semibold">{tierName || "a priority tier"}</span>{" "}
              for priority scheduling but haven't added any requests yet. Add at
              least one request below so we know what to prioritize — or continue
              without any.
            </p>
          </div>
          {onContinueWithoutRequests && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onContinueWithoutRequests}
              >
                Continue without requests
              </Button>
            </div>
          )}
        </div>
      )}

      {added.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Your scheduling requests ({added.length}) for {teamLabel}
          </div>
          {added.map((c: any, idx: number) => {
            const detail = formatConstraintDetail(c, cart, eventTeams);
            const fee = constraintFeeForDisplay(c, constraintTypePricing);
            return (
              <div
                key={`${c.template_id}-${idx}`}
                className="p-3 border rounded flex items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{c.template_label}</div>
                  {detail && (
                    <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>
                  )}
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 text-xs text-muted-foreground items-center">
                    <span>
                      {tierWaives
                        ? "Included"
                        : `Fee: ${fee > 0 ? money(fee) : "Free"}`}
                    </span>
                    {c.requires_review && <span>· Requires review</span>}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRequest(idx)}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {showUpsell && (
        <div className="p-3 border border-primary/40 bg-primary/5 rounded text-sm flex flex-wrap items-center justify-between gap-2">
          <div>
            You've added {added.length} requests at {money(requestFeesSum)}.
            Upgrade to Priority for {money(Number(priorityTierPrice))} and lock
            them all in first.
          </div>
          <Button type="button" size="sm" onClick={onUpgradeToPriority}>
            Upgrade to Priority
          </Button>
        </div>
      )}

      {visibleOfferings.length === 0 ? (
        <div className="p-3 border border-amber-300 bg-amber-50 rounded text-sm text-amber-800">
          No request types are available for this team yet. Some request types
          (like coach conflict) require at least one other team in your cart or
          already registered for the event.
        </div>
      ) : (
        <div className="p-4 border rounded-lg space-y-3 bg-muted/20">
          <div className="inline-flex items-center gap-2 rounded-md border border-cyan bg-cyan/5 px-3 py-2 text-sm font-semibold text-cyan">
            <Plus className="w-4 h-4" />
            {added.length > 0 ? "Add another request" : "Add a scheduling request"}
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Request type</Label>
            <Select
              // Remount after each saved request so the picker fully resets —
              // otherwise re-picking the same request type fires no change
              // event and coaches can't add a second request of that type.
              key={`req-type-${added.length}`}
              value={selectedTemplateId}
              onValueChange={(v) => selectTemplate(v)}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Select request type..." />
              </SelectTrigger>
              <SelectContent>
                {visibleOfferings.map((o) => {
                  const eff = effective(o);
                  return (
                    <SelectItem key={o.id} value={eff.template_id}>
                      {eff.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedEff?.description && (
              <p className="text-xs italic text-muted-foreground mt-1">
                {selectedEff.description}
              </p>
            )}
          </div>

          {selectedEff && selectedEff.input_field_specs.length > 0 && (
            <div className="space-y-3">
              {selectedEff.input_field_specs.map((spec) => {
                const required = spec.required !== false;
                const val = draftParams[spec.field_key];
                const setVal = (v: any) =>
                  setDraftParams((p) => ({ ...p, [spec.field_key]: v }));

                if (spec.input_type === "date") {
                  return (
                    <div key={spec.field_key} className="space-y-1">
                      <Label className="text-xs">
                        {spec.label} {required ? "*" : "(optional)"}
                      </Label>
                      <Input
                        type="date"
                        min={eventStartDate ?? undefined}
                        max={eventEndDate ?? undefined}
                        value={(val as string) ?? ""}
                        onChange={(e) => setVal(e.target.value)}
                      />
                      {spec.help_text && (
                        <p className="text-[11px] text-muted-foreground">{spec.help_text}</p>
                      )}
                    </div>
                  );
                }

                if (spec.input_type === "time") {
                  return (
                    <div key={spec.field_key} className="space-y-1">
                      <Label className="text-xs">
                        {spec.label} {required ? "*" : "(optional)"}
                      </Label>
                      <Input
                        type="time"
                        value={(val as string) ?? ""}
                        onChange={(e) => setVal(e.target.value)}
                      />
                      {spec.help_text && (
                        <p className="text-[11px] text-muted-foreground">{spec.help_text}</p>
                      )}
                    </div>
                  );
                }

                if (spec.input_type === "team_select") {
                  return (
                    <div key={spec.field_key} className="space-y-1">
                      <Label className="text-xs">
                        {spec.label} {required ? "*" : "(optional)"}
                      </Label>
                      <Select
                        value={(val as string) || undefined}
                        onValueChange={(v) => setVal(v)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Select team..." />
                        </SelectTrigger>
                        <SelectContent>
                          {teamOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {spec.help_text && (
                        <p className="text-[11px] text-muted-foreground">{spec.help_text}</p>
                      )}
                    </div>
                  );
                }

                if (spec.input_type === "team_multiselect") {
                  const selected: string[] = Array.isArray(val) ? val : [];
                  return (
                    <div key={spec.field_key} className="space-y-1">
                      <Label className="text-xs">
                        {spec.label} {required ? "*" : "(optional)"}
                      </Label>
                      <div className="space-y-1 border rounded p-2">
                        {teamOptions.map((opt) => {
                          const checked = selected.includes(opt.value);
                          return (
                            <label
                              key={opt.value}
                              className="flex items-center gap-2 text-sm cursor-pointer"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(c) => {
                                  if (c) setVal([...selected, opt.value]);
                                  else setVal(selected.filter((v) => v !== opt.value));
                                }}
                              />
                              {opt.label}
                            </label>
                          );
                        })}
                      </div>
                      {spec.help_text && (
                        <p className="text-[11px] text-muted-foreground">{spec.help_text}</p>
                      )}
                    </div>
                  );
                }

                if (spec.input_type === "team_search_select") {
                  const displayKey = `${spec.field_key}__display`;
                  return (
                    <div key={spec.field_key} className="space-y-1">
                      <Label className="text-xs">
                        {spec.label} {required ? "*" : "(optional)"}
                      </Label>
                      <TeamSearchSelect
                        value={(val as string) || ""}
                        displayValue={(draftParams[displayKey] as string) || ""}
                        onChange={(teamId, displayName) => {
                          setDraftParams((p) => ({
                            ...p,
                            [spec.field_key]: teamId,
                            [displayKey]: displayName,
                          }));
                        }}
                        ageGroup={undefined}
                        gender={item.gender ?? undefined}
                        helpText={spec.help_text}
                        cartOptions={teamOptions.filter((o) => o.value.startsWith("cart:"))}

                      />
                    </div>
                  );
                }

                if (spec.input_type === "team_search_multiselect") {
                  const displayKey = `${spec.field_key}__display`;
                  const selected: string[] = Array.isArray(val) ? val : [];
                  const displays =
                    (draftParams[displayKey] as Record<string, string>) || {};
                  return (
                    <div key={spec.field_key} className="space-y-1">
                      <Label className="text-xs">
                        {spec.label} {required ? "*" : "(optional)"}
                      </Label>
                      <TeamSearchMultiSelect
                        values={selected}
                        displayValues={displays}
                        onChange={(ids, dispMap) => {
                          setDraftParams((p) => ({
                            ...p,
                            [spec.field_key]: ids,
                            [displayKey]: dispMap,
                          }));
                        }}
                        ageGroup={undefined}
                        gender={item.gender ?? undefined}
                        helpText={spec.help_text}
                        cartOptions={teamOptions.filter((o) => o.value.startsWith("cart:"))}

                      />
                    </div>
                  );
                }

                return null;
              })}
            </div>
          )}

          {selectedEff && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  Request fee: {tierWaives ? "Included" : (selectedEff.fee > 0 ? money(selectedEff.fee) : "Free")}
                </span>
                {selectedEff.requires_review && (
                  <span className="ml-2">(Requires review by event staff)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => onUpdate({ scheduling_draft: null })}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={addRequest}
                  disabled={!requiredFilled}
                  className="bg-cyan text-cyan-foreground hover:bg-cyan-hover"
                >
                  <Plus className="w-4 h-4 mr-1" /> Save request
                </Button>
              </div>

            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Validates that all added constraints have their required fields filled.
function constraintsAreValidForItem(item: CartItem): boolean {
  for (const c of (item.requested_constraints ?? []) as any[]) {
    const specs: InputFieldSpec[] = c.input_field_specs ?? [];
    for (const s of specs) {
      if (s.required === false) continue;
      const v = c.params?.[s.field_key];
      if (s.input_type === "team_multiselect" || s.input_type === "team_search_multiselect") {
        if (!Array.isArray(v) || v.length === 0) return false;
      } else if (v === undefined || v === null || String(v).trim() === "") {
        return false;
      }
    }
  }
  return true;
}

// =================== STEP 4: Extras ===================

function ExtrasStep({
  cart,
  setCart,
  eventId,
  eventStartDate,
  eventEndDate,
}: {
  cart: Cart;
  setCart: (updater: any) => void;
  eventId: string;
  eventStartDate?: string | null;
  eventEndDate?: string | null;
}) {
  const teams = cart.items.filter(isItemComplete);

  const updateItemById = (clientId: string, patch: Partial<CartItem>) =>
    setCart((c: Cart | null) =>
      c
        ? {
            ...c,
            items: c.items.map((i) =>
              i.client_id === clientId ? { ...i, ...patch } : i
            ),
          }
        : c
    );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">
          Extras for your {teams.length} {teams.length === 1 ? "team" : "teams"}
        </h2>
        <p className="text-sm text-muted-foreground">
          Extras are purchased per team — anything you add below is billed to
          that team and attributed to it in your confirmation.
        </p>

      </div>

      {teams.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Add a team first to purchase extras.
        </p>
      )}

      {teams.map((it) => (
        <TeamExtrasBlock
          key={it.client_id}
          item={it}
          eventId={eventId}
          eventStartDate={eventStartDate}
          eventEndDate={eventEndDate}
          onUpdate={(patch) => updateItemById(it.client_id, patch)}
        />
      ))}
    </div>
  );
}

function TeamExtrasBlock({
  item,
  eventId,
  eventStartDate,
  eventEndDate,
  onUpdate,
}: {
  item: CartItem;
  eventId: string;
  eventStartDate?: string | null;
  eventEndDate?: string | null;
  onUpdate: (p: Partial<CartItem>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [playerDialog, setPlayerDialog] = useState<{ extra: any } | null>(null);
  const hasIncomplete = (item.extras ?? []).some(
    (e) =>
      Number(e.qty || 0) > 0 &&
      ((e.requires_team && !e.team_name) ||
        (e.requires_player && (!e.player_name || !e.player_jersey_number)) ||
        (e.requires_game_date && !e.game_date))
  );
  useEffect(() => {
    if (hasIncomplete) setOpen(true);
  }, [hasIncomplete]);



  const { data: extras } = useQuery({
    queryKey: ["event-extras", eventId],
    queryFn: async () => {
      const { data } = await sb
        .from("event_extras")
        .select("*")
        .eq("event_id", eventId)
        .eq("is_active", true);
      return data ?? [];
    },
  });

  const ownTeamName = (item.team_display_name || item.team_name || "").trim();
  const ownDivision = item.division_label || null;

  if (!extras || extras.length === 0) {
    return (
      <div className="rounded-lg border border-border p-4">
        <h3 className="text-base font-semibold">{teamHeading(item)}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          No extras for this event.
        </p>
      </div>
    );
  }

  const setQty = (extra: any, qty: number) => {
    const remaining = item.extras.filter((e) => e.extra_id !== extra.id);
    if (qty > 0) {
      const existing = item.extras.find((e) => e.extra_id === extra.id);
      remaining.push({
        extra_id: extra.id,
        name: extra.name,
        qty,
        price: Number(extra.price),
        requires_team: !!extra.requires_team,
        requires_player: !!extra.requires_player,
        requires_game_date: !!extra.requires_game_date,
        // Extras always belong to the team whose block they were bought in.
        team_client_id: item.client_id,
        team_id: item.team_id ?? null,
        team_name: ownTeamName || null,
        division: ownDivision,
        player_name: existing?.player_name ?? null,
        player_jersey_number: existing?.player_jersey_number ?? null,
        game_date: existing?.game_date ?? null,
      });
    }
    onUpdate({ extras: remaining });
  };

  const updateField = (extraId: string, patch: Partial<CartExtra>) => {
    onUpdate({
      extras: item.extras.map((e) =>
        e.extra_id === extraId ? { ...e, ...patch } : e
      ),
    });
  };

  const extrasTotal = item.extras.reduce(
    (s, e) => s + Number(e.price || 0) * Number(e.qty || 0),
    0
  );

  const extrasCount = item.extras.reduce((s, e) => s + Number(e.qty || 0), 0);
  const summaryText =
    extrasCount === 0
      ? "No extras"
      : `${extrasCount} extra${extrasCount === 1 ? "" : "s"}${
          extrasTotal > 0 ? ` · ${money(extrasTotal)}` : ""
        }`;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{teamHeading(item)}</h3>
          <p className="text-xs text-muted-foreground">
            {summaryText} · Team total {money(itemTotal(item))}
          </p>
        </div>
        <Button
          type="button"
          variant={open ? "outline" : "default"}
          size="sm"
          className="shrink-0"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? (
            <>
              <ChevronUp className="w-4 h-4 mr-1" /> Hide
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-1" /> Add an extra
            </>
          )}
        </Button>
      </div>

      {open && (
      <>


      {extras.map((e: any) => {
        const current = item.extras.find((x) => x.extra_id === e.id);
        const qty = current?.qty ?? 0;
        // Team-level extras only need a quantity — never player details.
        const isTeamLevel = (e.level ?? "team") === "team";
        const needsTeam = !!e.requires_team;
        const needsPlayer = !isTeamLevel && !!e.requires_player;
        const needsDate = !!e.requires_game_date;
        const hasConditional = needsTeam || needsPlayer || needsDate;
        const showFields = qty > 0 && hasConditional;

        const missing =
          qty > 0 &&
          ((needsTeam && !current?.team_name) ||
            (needsPlayer &&
              (!current?.player_name || !current?.player_jersey_number)) ||
            (needsDate && !current?.game_date));

        return (
          <div
            key={e.id}
            className="p-3 border border-border rounded space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-sm">{e.name}</div>
                {e.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line">
                    {e.description}
                  </p>
                )}
                <div className="text-xs text-muted-foreground mt-1">
                  {money(Number(e.price))}
                </div>
              </div>
              <Input
                type="number"
                min={0}
                value={qty}
                onChange={(ev) => setQty(e, parseInt(ev.target.value) || 0)}
                className="w-20"
              />
            </div>

            {showFields && (
              <div className="space-y-3 pt-1 border-t border-border/60">
                {needsTeam && (
                  <div>
                    <Label className="text-xs">Team</Label>
                    <div className="mt-1 rounded border border-border bg-muted/40 px-3 py-2 text-sm">
                      {ownTeamName || "This team"}
                      {ownDivision ? ` · ${ownDivision}` : ""}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Buying this for another team? Add it in that team's block
                      below.
                    </p>
                  </div>
                )}


                {needsPlayer && (
                  <div className="space-y-2">
                    <Label className="text-xs">Player details *</Label>
                    {current?.player_name ? (
                      <div className="flex items-center justify-between gap-2 rounded border border-border bg-muted/40 px-3 py-2">
                        <div className="text-sm min-w-0 truncate">
                          {current.player_jersey_number ? `#${current.player_jersey_number} · ` : ""}
                          {current.player_name}
                          {current.player_instagram_handle ? ` · ${displayHandle(current.player_instagram_handle)}` : ""}
                          {current.player_x_handle ? ` · ${displayHandle(current.player_x_handle)}` : ""}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="shrink-0 h-9"
                          onClick={() => setPlayerDialog({ extra: e })}
                        >
                          Edit
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-11 w-full"
                        onClick={() => setPlayerDialog({ extra: e })}
                      >
                        Add player details
                      </Button>
                    )}
                  </div>
                )}

                {needsDate && (
                  <div>
                    <Label className="text-xs">Game date *</Label>
                    <Input
                      type="date"
                      className="mt-1"
                      value={current?.game_date ?? ""}
                      min={eventStartDate ?? undefined}
                      max={eventEndDate ?? undefined}
                      onChange={(ev) =>
                        updateField(e.id, { game_date: ev.target.value })
                      }
                    />
                  </div>
                )}

                {missing && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Fill in the required details above before continuing.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
      </>
      )}

      {/* One player-details form, shared with /event-extras and the Live Sheet. */}
      <PlayerExtraDialog
        open={!!playerDialog}
        onOpenChange={(o) => { if (!o) setPlayerDialog(null); }}
        extra={playerDialog?.extra ?? null}
        teamLabel={[ownTeamName, ownDivision].filter(Boolean).join(" · ")}
        roster={[]}
        allowFreeTextPlayer
        requireHandles={false}
        initial={
          playerDialog
            ? item.extras.find((x) => x.extra_id === playerDialog.extra.id) ?? null
            : null
        }
        onSubmit={(d) => {
          if (!playerDialog) return;
          updateField(playerDialog.extra.id, {
            player_name: d.player_name ?? null,
            player_jersey_number: d.player_jersey_number ?? null,
            player_instagram_handle: d.player_instagram_handle ?? null,
            player_x_handle: d.player_x_handle ?? null,
          });
        }}
      />
    </div>

  );
}

// =================== STEP 5: Payment ===================

function PaymentStep({
  cart,
  setCart,
  event,
}: {
  cart: Cart;
  setCart: (c: Cart) => void;
  event: any;
}) {
  const methods: string[] = event?.pay_later_methods ?? [];
  const due = dueDateFor(event);
  const lateFee = Number(event?.pay_later_late_fee ?? 0);
  const method = cart.payment_method_chosen ?? "online";
  const isLater = method !== "online";
  const onUpdate = (p: Partial<Cart>) => setCart({ ...cart, ...p });


  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">How will you pay?</h2>
      <p className="text-sm text-muted-foreground">
        One payment method covers every team in this registration.
      </p>
      <RadioGroup
        value={method}
        onValueChange={(v) =>
          onUpdate({
            payment_method_chosen: v as PaymentMethod,
            ...(v === "online" ? { payment_promise_notes: undefined } : {}),
          })
        }
      >

        <label className="flex items-start gap-3 p-3 min-h-[48px] border border-border rounded cursor-pointer">
          <RadioGroupItem value="online" />
          <div>
            <div className="font-medium text-sm">Pay online now</div>
            <div className="text-xs text-muted-foreground">
              Credit/debit via Stripe
            </div>
          </div>
        </label>

        {methods
          .filter((m) => m !== "cash")
          .map((m) => (
            <label
              key={m}
              className="flex items-start gap-3 p-3 min-h-[48px] border border-border rounded cursor-pointer"
            >
              <RadioGroupItem value={m} />
              <div>
                <div className="font-medium text-sm capitalize">Pay later — {m}</div>
              </div>
            </label>
          ))}
      </RadioGroup>

      {isLater && (
        <div className="space-y-3 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded">
          {method === "venmo" && (
            <div>
              {event?.pay_later_venmo_handle && (
                <>
                  <p className="text-xs font-medium uppercase tracking-wide mb-1">
                    Send your Venmo payment to
                  </p>
                  <p className="text-base font-semibold">
                    @{String(event.pay_later_venmo_handle).replace(/^@/, "")}
                  </p>
                </>
              )}
              {event?.pay_later_venmo_note && (
                <p className="text-sm mt-2">{event.pay_later_venmo_note}</p>
              )}
            </div>
          )}
          {method === "check" && event?.pay_later_check_instructions && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide mb-1">
                Paying by check
              </p>
              <p className="text-sm">{event.pay_later_check_instructions}</p>
            </div>
          )}
          {method !== "check" && event?.pay_later_instructions && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide mb-1">
                Payment instructions
              </p>
              <RichTextDisplay
                html={event.pay_later_instructions}
                className="text-sm"
              />
            </div>

          )}
          {due && (
            <p className="text-sm">
              <strong>Payment due by {due.toLocaleDateString()}</strong>
            </p>
          )}
          {lateFee > 0 && (
            <p className="text-xs text-red-700">
              ⚠ Late fee of {money(lateFee)} applies if not paid by deadline.
            </p>
          )}
          <div>
            <Label>Notes to organizer (optional)</Label>
            <Textarea
              value={cart.payment_promise_notes ?? ""}
              onChange={(e) => onUpdate({ payment_promise_notes: e.target.value })}
              placeholder="e.g., Will mail check Tuesday from coach Spradling's office"
              rows={2}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// =================== STEP 6: Review ===================

function ReviewStep({
  cart,
  setCart,
  onAddTeam,
  onEditTeamZip,
  onSubmit,
  submitting,
  onlineTotal,
  laterTotal,
  event,
  autoDiscount,
}: {
  cart: Cart;
  setCart: (c: Cart) => void;
  onAddTeam: () => void;
  onEditTeamZip: (clientId: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  onlineTotal: number;
  laterTotal: number;
  event?: any;
  autoDiscount?: {
    amount: number;
    label: string;
    perTeam?: Record<string, number>;
  } | null;
}) {
  const method = cart.payment_method_chosen ?? "online";
  const payingLater = method !== "online";
  const due = payingLater ? dueDateFor(event) : null;
  const submitLabel = payingLater
    ? `Place Registration (${money(laterTotal)} — pay later via ${method})`
    : onlineTotal === 0
    ? "Submit Registration (no payment required now)"
    : `Pay & Register (${money(onlineTotal)})`;

  // Only completed teams are reviewed, validated or submitted — the trailing
  // blank placeholder the cart always keeps must be invisible here.
  const reviewItems = cart.items.filter(isItemComplete);

  // Duplicate scheduling requests within a team (same template + same params).
  const dupKey = (c: any) =>
    `${c.template_id}::${JSON.stringify(c.params ?? {})}`;
  const duplicatesFor = (i: CartItem) => {
    const seen = new Set<string>();
    const dups: any[] = [];
    for (const c of i.requested_constraints ?? []) {
      const k = dupKey(c);
      if (seen.has(k)) dups.push(c);
      else seen.add(k);
    }
    return dups;
  };
  const removeDuplicates = (clientId: string) => {
    setCart({
      ...cart,
      items: cart.items.map((i) => {
        if (i.client_id !== clientId) return i;
        const seen = new Set<string>();
        return {
          ...i,
          requested_constraints: (i.requested_constraints ?? []).filter((c) => {
            const k = dupKey(c);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          }),
        };
      }),
    });
  };

  const blockingReason =
    reviewItems.length === 0
      ? "Add at least one team to continue."
      : reviewItems.some((i) => !isValidHomeZip(i.team_home_zip))
      ? "Enter a 5-digit Team home ZIP for every team (step 1)."
      : reviewItems.some((i) => !i.coach_email)
      ? "Add a coach email for every team (step 2)."
      : reviewItems.some(
          (i) =>
            !i.registrant_is_coach &&
            (!i.coach_phone || phoneDigits(i.coach_phone).length < 10)
        )
      ? "Add a coach phone for every team (step 2)."
      : reviewItems.some((i) => !constraintsAreValidForItem(i))
      ? "One of your scheduling requests is missing required details (step 3)."
      : null;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Review your registration</h2>

      <div className="p-3 border border-border rounded text-sm">
        <span className="font-medium">Payment: </span>
        {payingLater
          ? `Pay later via ${method}${
              due ? ` (due ${due.toLocaleDateString()})` : ""
            } — ${money(laterTotal)}`
          : `Pay online now — ${money(onlineTotal)}`}
      </div>

      <div className="space-y-3">
        {reviewItems.map((i) => {
          const dups = duplicatesFor(i);
          return (
            <div key={i.client_id} className="p-3 border border-border rounded">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <div className="font-medium break-words">{i.team_name}</div>
                <span className="text-sm text-muted-foreground">Team home ZIP: {i.team_home_zip || "Missing"}</span>
                <Button type="button" variant="link" className="min-h-11 px-1" aria-label={`Edit Team home ZIP for ${i.team_name}`} onClick={() => onEditTeamZip(i.client_id)}>Edit</Button>
              </div>
              <div className="text-xs text-muted-foreground">
                {i.event_name} · {i.division_label}
              </div>
              <div className="text-xs mt-1">
                Coach: {i.coach_name} ({i.coach_email})
              </div>
              <ul className="mt-2 space-y-0.5">
                <li className="text-[11px] text-muted-foreground">
                  Entry fee: {money(Number(i.base_price || 0))}
                </li>
                {(i.requested_constraints ?? []).map((c: any, ci: number) => (
                  <li key={`c-${ci}`} className="text-[11px] text-muted-foreground">
                    Request: {c.template_label}{" "}
                    {tierWaivesFees(i) ? "(Included)" : `+${money(Number(c.fee || 0))}`}
                  </li>
                ))}
                {Number(i.scheduling_tier_fee || 0) > 0 && (
                  <li className="text-[11px] text-muted-foreground">
                    Scheduling tier: {TIER_LABEL[i.scheduling_tier ?? "standard"]} +
                    {money(Number(i.scheduling_tier_fee || 0))}
                  </li>
                )}
                {(i.extras ?? [])
                  .filter((e: any) => Number(e.qty || 0) > 0)
                  .map((e: any, ei: number) => (
                    <li key={`e-${ei}`} className="text-[11px] text-muted-foreground">
                      Extra: {e.name}
                      {Number(e.qty) > 1 ? ` ×${e.qty}` : ""} +
                      {money(Number(e.price || 0) * Number(e.qty || 0))}
                    </li>
                  ))}
              </ul>
              {Number(autoDiscount?.perTeam?.[i.client_id] ?? 0) > 0 && (
                <div className="text-[11px] text-green-700">
                  Discount: −{money(Number(autoDiscount!.perTeam![i.client_id]))}
                </div>
              )}
              <div className="text-sm font-medium mt-1">
                {money(
                  Math.max(
                    0,
                    itemTotal(i) - Number(autoDiscount?.perTeam?.[i.client_id] ?? 0)
                  )
                )}
              </div>

              {dups.length > 0 && (
                <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  <p className="font-medium">
                    {dups.length} duplicate scheduling request
                    {dups.length > 1 ? "s" : ""} detected
                  </p>
                  <p className="mt-0.5">
                    {dups.map((d: any) => d.template_label).join(", ")} —
                    you're being charged more than once for the same request.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() => removeDuplicates(i.client_id)}
                  >
                    Remove duplicates
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>


      {(() => {
        const entryFees = reviewItems.reduce(
          (s, i) => s + Number(i.base_price || 0),
          0
        );
        const otherCharges = reviewItems.reduce(
          (s, i) => s + itemTotal(i) - Number(i.base_price || 0),
          0
        );
        const disc = autoDiscount?.amount ?? 0;
        const coupon = cart.coupon_applied?.discount_amount ?? 0;
        const total = Math.max(0, entryFees + otherCharges - disc - coupon);
        const fee = payingLater ? 0 : Math.round(total * CARD_FEE_RATE * 100) / 100;
        return (
          <div className="p-3 border border-border rounded text-sm space-y-1">
            <div className="flex justify-between">
              <span>Entry fees</span>
              <span>{money(entryFees)}</span>
            </div>
            {disc > 0 && (
              <div className="flex justify-between text-green-700">
                <span>{autoDiscount!.label}</span>
                <span>−{money(disc)}</span>
              </div>
            )}
            {otherCharges > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Scheduling requests, tiers &amp; extras (not discounted)</span>
                <span>{money(otherCharges)}</span>
              </div>
            )}
            {coupon > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Coupon ({cart.coupon_applied?.code})</span>
                <span>−{money(coupon)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-1">
              <span>Order total</span>
              <span>{money(total)}</span>
            </div>
            {fee > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Card processing fee (3%)</span>
                <span>{money(fee)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold border-t border-border pt-1">
              <span>{fee > 0 ? "Total charged today" : "Total"}</span>
              <span>{money(total + fee)}</span>
            </div>
          </div>
        );
      })()}

      <CouponBlock cart={cart} setCart={setCart} />

      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={onAddTeam}>
          <Plus className="w-3 h-3 mr-1" /> Add another team
        </Button>
      </div>

      <Button
        size="lg"
        className="w-full"
        onClick={onSubmit}
        disabled={submitting || !!blockingReason}
      >
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {submitLabel}
      </Button>
      {blockingReason && (
        <p className="text-xs text-amber-700 text-center">{blockingReason}</p>
      )}
    </div>
  );

}

// =================== Coupon Block ===================

function CouponBlock({
  cart,
  setCart,
}: {
  cart: Cart;
  setCart: (c: Cart) => void;
}) {
  const { user } = useAuth();
  const [code, setCode] = useState(cart.coupon_code ?? "");
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    setError(null);
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setValidating(true);
    try {
      const firstEventId = cart.items[0]?.event_id;
      if (!firstEventId) {
        setError("Add a team first");
        return;
      }
      // Coupons are readable only by org staff, so registrants validate through
      // a security-definer RPC scoped to the event's organisation.
      const { data: rows, error: rpcErr } = await (sb as any).rpc(
        "validate_event_coupon",
        { p_code: trimmed, p_event_id: firstEventId }
      );
      if (rpcErr) {
        console.error("[coupon] validate_event_coupon failed", rpcErr);
        setError("Could not validate that code. Please try again.");
        return;
      }
      const c = Array.isArray(rows) ? rows[0] : rows;
      if (!c) {
        setError("Invalid coupon code");
        return;
      }
      const now = Date.now();
      if (c.valid_from && new Date(c.valid_from).getTime() > now)
        return setError("Coupon not yet valid");
      if (c.valid_until && new Date(c.valid_until).getTime() < now)
        return setError("Coupon expired");
      if (c.max_uses != null && c.used_count >= c.max_uses)
        return setError("Coupon usage limit reached");
      if (
        c.scope === "specific_events" &&
        !(c.scoped_event_ids ?? []).some((eid: string) =>
          cart.items.some((it) => it.event_id === eid)
        )
      )
        return setError("Coupon does not apply to events in your cart");
      if (c.scoped_user_id && c.scoped_user_id !== user?.id)
        return setError("Coupon not valid for this user");
      if (
        c.scoped_email &&
        c.scoped_email.toLowerCase() !== (user?.email ?? "").toLowerCase()
      )
        return setError("Coupon not valid for this email");
      if (c.max_uses_per_user != null && user?.id) {
        const { count } = await sb
          .from("coupon_redemptions")
          .select("id", { count: "exact", head: true })
          .eq("coupon_id", c.id)
          .eq("redeemed_by_user_id", user.id);
        if ((count ?? 0) >= c.max_uses_per_user)
          return setError("You have already used this coupon");
      }

      const appliesTo = (c as any).applies_to ?? "order_total";
      const isPercent =
        c.discount_type === "percent" || c.discount_type === "percentage";
      const perTeamFixed =
        !isPercent && ((c as any).fixed_scope ?? "per_order") === "per_team";
      // Basis per cart item — "entry_fees" coupons never touch extras,
      // scheduling requests or tier fees.
      const basisFor = (it: (typeof cart.items)[number]) => {
        if (
          c.scope === "specific_events" &&
          !(c.scoped_event_ids ?? []).includes(it.event_id)
        )
          return 0;
        return appliesTo === "entry_fees"
          ? Number(it.base_price || 0)
          : itemTotal(it);
      };
      const bases = cart.items.map(basisFor);
      const eligibleSubtotal = bases.reduce((s, v) => s + v, 0);
      const value = Number(c.discount_value);
      const perItem = bases.map((b) => {
        if (b <= 0 || eligibleSubtotal <= 0) return 0;
        if (isPercent) return +((b * value) / 100).toFixed(2);
        if (perTeamFixed) return +Math.min(value, b).toFixed(2);
        return +((Math.min(value, eligibleSubtotal) * b) / eligibleSubtotal).toFixed(2);
      });
      const discountAmount = +perItem.reduce((s, v) => s + v, 0).toFixed(2);
      const perTeamMap: Record<string, number> = {};
      cart.items.forEach((it, idx) => {
        if (perItem[idx] > 0) perTeamMap[it.client_id] = perItem[idx];
      });

      setCart({
        ...cart,
        coupon_code: trimmed,
        coupon_applied: {
          code: trimmed,
          description: c.description,
          discount_type: c.discount_type,
          discount_value: value,
          discount_amount: discountAmount,
          applies_to: appliesTo,
          fixed_scope: (c as any).fixed_scope ?? "per_order",
          overrides_policies: (c as any).overrides_policies === true,
          per_team: perTeamMap,
        },
      });

    } finally {
      setValidating(false);
    }
  };

  const remove = () => {
    setCode("");
    setError(null);
    setCart({ ...cart, coupon_code: undefined, coupon_applied: null });
  };

  if (cart.coupon_applied) {
    const a = cart.coupon_applied;
    return (
      <div className="p-3 border border-green-300 bg-green-50 dark:bg-green-950 rounded">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm">
            <Check className="w-3 h-3 inline text-green-700 mr-1" />
            <strong>{a.code}</strong> applied
            {a.description ? ` — ${a.description}` : ""} (
            {a.discount_type === "percent" || a.discount_type === "percentage"
              ? `${a.discount_value}% off`
              : `${money(a.discount_value)} off`}
            ): −{money(a.discount_amount)}
          </div>
          <Button size="sm" variant="ghost" onClick={remove}>
            Remove
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Label>Coupon code</Label>
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onBlur={() => code.trim() && apply()}
          placeholder="ENTER CODE"
        />
        <Button
          variant="outline"
          onClick={apply}
          disabled={validating || !code.trim()}
        >
          {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
        </Button>
      </div>
      {error && (
        <div className="text-xs text-red-700 mt-1 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {error}
        </div>
      )}
    </div>
  );
}

// =================== STEP: Account (inline signup/signin) ===================

function AccountStep({
  cart,
  setCart,
  onAuthed,
}: {
  cart: Cart;
  setCart: (c: Cart) => void;
  onAuthed: () => void;
}) {
  const [mode, setMode] = useState<"signup" | "signin">("signup");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accountType, setAccountType] = useState<"parent" | "coach" | "director" | "athlete">("parent");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState<{ signedInEmail: string } | null>(null);

  const editField = (key: keyof Cart, value: string) => {
    setCart({ ...cart, [key]: value } as Cart);
  };

  const passwordStrength = (() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s; // 0-4
  })();

  const strengthLabel = ["Too short", "Weak", "Fair", "Good", "Strong"][passwordStrength];
  const strengthColor =
    passwordStrength <= 1
      ? "bg-red-500"
      : passwordStrength === 2
      ? "bg-amber-500"
      : passwordStrength === 3
      ? "bg-blue-500"
      : "bg-green-500";

  const doSignup = async () => {
    setError(null);
    if (!cart.registrant_first_name || !cart.registrant_last_name) {
      setError("First and last name are required (go back to Your Contact Details)");
      return;
    }
    if (!cart.registrant_email) {
      setError("Email is required (go back to Your Contact Details)");
      return;
    }
    if (!cart.registrant_phone || phoneDigits(cart.registrant_phone).length < 10) {
      setError("Phone number is required (go back to Your Contact Details)");
      return;
    }
    if (passwordStrength < 2) {
      setError("Password is too weak");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    if (!agree) {
      setError("You must agree to the Terms of Service");
      return;
    }
    setBusy(true);
    try {
      const fullName =
        `${cart.registrant_first_name} ${cart.registrant_last_name}`.trim();
      const { data: ssoData, error: ssoError } = await invokeEdgeFunction(
        'sso-sign-up',
        {
          body: {
            email: cart.registrant_email,
            password,
            first_name: cart.registrant_first_name,
            last_name: cart.registrant_last_name,
            phone: cart.registrant_phone ?? "",
            account_type: accountType,
          },
        }
      );
      if (ssoError) {
        setError(ssoError.message || 'Sign-up failed');
        return;
      }
      if ((ssoData as any)?.error) {
        const msg = (ssoData as any).error as string;
        if (/already|registered|exists/i.test(msg)) {
          setMode("signin");
          setError("That email is already registered. Please sign in below.");
        } else {
          setError(msg);
        }
        return;
      }
      if ((ssoData as any)?.email_confirmation_required) {
        setError(`Check your email at ${cart.registrant_email} to confirm your account, then come back to complete registration.`);
        setMode("signin");
        return;
      }
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: (ssoData as any).hashed_token,
        type: 'magiclink',
      });
      if (verifyError) {
        setError(verifyError.message);
        return;
      }
      onAuthed();
    } catch (err: any) {
      setError(err.message ?? "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  const doSignin = async () => {
    setError(null);
    setBusy(true);
    try {
      const { data: ssoData, error: ssoError } = await invokeEdgeFunction(
        'sso-sign-in',
        { body: { email: cart.registrant_email, password } }
      );
      if (ssoError) {
        setError(ssoError.message || 'Sign-in failed');
        return;
      }
      if ((ssoData as any)?.error) {
        const msg = (ssoData as any).error as string;
        if (/RECRUITLOOK_ANON_KEY/i.test(msg)) {
          setError('Authentication temporarily unavailable, please try again');
        } else {
          setError(msg);
        }
        return;
      }
      const { data: sessionData, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: (ssoData as any).hashed_token,
        type: 'magiclink',
      });
      if (verifyError) {
        setError(verifyError.message);
        return;
      }
      const signedInEmail = sessionData.user?.email ?? "";
      if (
        signedInEmail &&
        cart.registrant_email &&
        signedInEmail.toLowerCase() !== cart.registrant_email.toLowerCase()
      ) {
        setMismatch({ signedInEmail });
        return;
      }
      onAuthed();
    } catch (err: any) {
      setError(err.message ?? "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  if (mismatch) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Email mismatch</h2>
        <p className="text-sm">
          You started this cart with <strong>{cart.registrant_email}</strong>{" "}
          but signed in as <strong>{mismatch.signedInEmail}</strong>.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => {
              setCart({ ...cart, registrant_email: mismatch.signedInEmail });
              setMismatch(null);
              onAuthed();
            }}
          >
            Use my signed-in account
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              await supabase.auth.signOut();
              setMismatch(null);
            }}
          >
            Cancel and use {cart.registrant_email}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Account</h2>

      <RadioGroup
        value={mode}
        onValueChange={(v) => {
          setMode(v as any);
          setError(null);
        }}
        className="grid grid-cols-2 gap-2"
      >
        <label className="flex items-center gap-3 p-3 min-h-[48px] border border-border rounded cursor-pointer">
          <RadioGroupItem value="signup" />
          <span className="text-sm">Create account</span>
        </label>
        <label className="flex items-center gap-3 p-3 min-h-[48px] border border-border rounded cursor-pointer">
          <RadioGroupItem value="signin" />
          <span className="text-sm">Sign in</span>
        </label>
      </RadioGroup>

      {mode === "signup" ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>First name</Label>
              <Input
                value={cart.registrant_first_name ?? ""}
                onChange={(e) => editField("registrant_first_name", e.target.value)}
              />
            </div>
            <div>
              <Label>Last name</Label>
              <Input
                value={cart.registrant_last_name ?? ""}
                onChange={(e) => editField("registrant_last_name", e.target.value)}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={cart.registrant_email ?? ""}
                readOnly
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">
                From your contact details. To use a different address, go back to
                Your Contact Details and change it there.
              </p>
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                type="tel"
                formatPhone
                placeholder="xxx-xxx-xxxx"
                value={formatPhoneDisplay(cart.registrant_phone ?? "")}
                onChange={(e) => editField("registrant_phone", phoneDigits(e.target.value))}
              />

            </div>
          </div>
          <div>
            <Label>Password *</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {password && (
              <div className="mt-1">
                <div className="h-1 w-full bg-muted rounded">
                  <div
                    className={`h-1 rounded ${strengthColor}`}
                    style={{ width: `${(passwordStrength / 4) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{strengthLabel}</p>
              </div>
            )}
          </div>
          <div>
            <Label>Confirm password *</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <div>
            <Label>Account type</Label>
            <RadioGroup
              value={accountType}
              onValueChange={(v) => setAccountType(v as any)}
              className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1"
            >
              {(["parent", "coach", "director", "athlete"] as const).map((t) => (
                <label
                  key={t}
                  className="flex items-center gap-2 p-2 border border-border rounded cursor-pointer text-sm capitalize"
                >
                  <RadioGroupItem value={t} />
                  {t}
                </label>
              ))}
            </RadioGroup>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} />
            <span>
              I agree to the{" "}
              <a href="/terms" target="_blank" className="underline">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" className="underline">
                Privacy Policy
              </a>
            </span>
          </label>
          <p className="text-xs text-muted-foreground">
            Your cart is saved. We'll create your account when you click below.
          </p>
          {error && (
            <div className="p-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          )}
          <Button onClick={doSignup} disabled={busy} className="w-full" size="lg">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Create Account & Continue
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={cart.registrant_email ?? ""}
              onChange={(e) => editField("registrant_email", e.target.value)}
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <a
            href="https://recruitlook.com/forgot-password"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs underline text-primary"
          >
            Forgot password?
          </a>
          {error && (
            <div className="p-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          )}
          <Button onClick={doSignin} disabled={busy} className="w-full" size="lg">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Sign in & Continue
          </Button>
        </div>
      )}
    </div>
  );
}

function PoolStep({
  cart,
  setCart,
  levels,
  helpText,
  required,
}: {
  cart: Cart;
  setCart: (c: Cart) => void;
  levels: Array<{ id: string; label: string }>;
  helpText?: string | null;
  required?: boolean;
}) {
  const setPool = (clientId: string, pool: string | null) => {
    setCart({
      ...cart,
      items: cart.items.map((i) =>
        i.client_id === clientId ? { ...i, pool } : i
      ),
    });
  };
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Select Pool / Level</h2>
        {helpText ? (
          <p className="text-sm text-muted-foreground mt-1">{helpText}</p>
        ) : (
          <p className="text-sm text-muted-foreground mt-1">
            Choose a competitive level for each team.
          </p>
        )}
      </div>
      <div className="space-y-3">
        {cart.items.map((i) => (
          <div key={i.client_id} className="flex items-center gap-3 border rounded-md p-3">
            <div className="flex-1">
              <div className="font-medium">{i.team_display_name || i.team_name || "Team"}</div>
              <div className="text-xs text-muted-foreground">{i.division_label}</div>
            </div>
            <Select
              value={i.pool ?? ""}
              onValueChange={(v) => setPool(i.client_id, v || null)}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder={required ? "Select pool *" : "Select pool"} />
              </SelectTrigger>
              <SelectContent>
                {levels.map((l) => (
                  <SelectItem key={l.id} value={l.label}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}
